import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js'
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js'

/**
 * Canvas / Scene
 */
const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()

/**
 * Renderer (black background)
 */
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false })
renderer.setClearColor(0x000000, 1)
renderer.domElement.style.touchAction = 'none'

/**
 * Loading manager
 */
const loadingManager = new THREE.LoadingManager()
const gltfLoader = new GLTFLoader(loadingManager)

/**
 * Textures
 */
const textureLoader = new THREE.TextureLoader(loadingManager)
const matcapTexture = textureLoader.load('/textures/matcaps/3.png')
matcapTexture.colorSpace = THREE.SRGBColorSpace
const baseMaterial = new THREE.MeshMatcapMaterial({ matcap: matcapTexture })
const donutMatcaps = Array.from({ length: 7 }, (_, i) => {
  const texture = textureLoader.load(`/textures/matcaps/${i + 1}.png`)
  texture.colorSpace = THREE.SRGBColorSpace
  return texture
})
let donutMatcapIndex = 6
const donutBaseMaterial = new THREE.MeshMatcapMaterial({ matcap: donutMatcaps[donutMatcapIndex] })
const linkMatcapTexture = textureLoader.load('/textures/matcaps/9.png')
linkMatcapTexture.colorSpace = THREE.SRGBColorSpace
const linkBaseMaterial = new THREE.MeshMatcapMaterial({ matcap: linkMatcapTexture })
const heroTexture = textureLoader.load('/images/self_1.png')
heroTexture.colorSpace = THREE.SRGBColorSpace
heroTexture.wrapS = THREE.ClampToEdgeWrapping
heroTexture.wrapT = THREE.ClampToEdgeWrapping
heroTexture.magFilter = THREE.NearestFilter
heroTexture.minFilter = THREE.NearestFilter
heroTexture.generateMipmaps = false

/**
 * Sizes
 */
const sizes = { width: window.innerWidth, height: window.innerHeight }
const mobileQuery = window.matchMedia('(max-width: 768px)')
const hoverCapableQuery = window.matchMedia('(hover: hover)')

function getPixelRatio() {
  const cap = mobileQuery.matches ? 2 : 2
  return Math.min(window.devicePixelRatio, cap)
}

/**
 * Mobile loader
 */
const mobileLoader = document.querySelector('.mobile-loader')
const loaderStart = performance.now()
const MOBILE_LOADER_MIN_MS = 1900
const ENABLE_BOOT_LOADER = false
let texturesReady = false
let fontReady = false
const tapOverlay = document.querySelector('.tap-overlay')
let tapOverlayShown = false
const bodyEl = document.body
let startupSpinPending = false
let startupSpinPlayed = false
const startupTextMeshes = []
let introActive = false
let introStart = 0
const INTRO_DURATION = 1.45
let introDuration = INTRO_DURATION
const introFromPosition = new THREE.Vector3()
const introFromTarget = new THREE.Vector3()
const aboutCard = document.querySelector('.about-card')
const aboutCloseBtn = aboutCard?.querySelector('.about-card__close')
let aboutState = 'closed'
let aboutTransitionStart = 0
const ABOUT_TRANSITION_SEC = 0.6
let aboutPlane = null
let aboutPlaneTexture = null
let aboutPlaneCanvas = null
let aboutPlaneCtx = null
let aboutPlaneImage = null
let aboutPanelPlane = null
let aboutPanelBackPlane = null
let aboutPanelTexture = null
let aboutPanelCanvas = null
let aboutPanelCtx = null
let aboutPanelContentPlane = null
let aboutPanelContentTexture = null
let aboutPanelContentCanvas = null
let aboutPanelContentCtx = null
let aboutPanelImagePlane = null
let aboutPanelTextGroup = null
let aboutText3DEnabled = false
let aboutPrefer3DText = false
let aboutDonutMesh = null
const aboutDonuts = []
const aboutDonutDummy = new THREE.Object3D()
const aboutCloseFromPosition = new THREE.Vector3()
const aboutCloseFromTarget = new THREE.Vector3()
let reelState = 'closed'
let reelTransitionStart = 0
const REEL_TRANSITION_SEC = 0.6
let reelPlane = null
let reelPlaneTexture = null
let reelPlaneCanvas = null
let reelPlaneCtx = null
let reelPlaneImage = null
let reelImageReady = false
const REEL_CANVAS_SIZE = { width: 1024, height: 640 }
const REEL_BASE_SIZE = { width: 1.4, height: 1.4 }
const REEL_TARGET = new THREE.Vector3(0, 0, 0)
const REEL_CAMERA_POSITION = new THREE.Vector3(0, 0, 2.4)
const REEL_URL = 'https://youtu.be/Sgx2FqIqHw8'
const REEL_THUMB_URL = 'https://img.youtube.com/vi/Sgx2FqIqHw8/hqdefault.jpg'
const reelCloseFromPosition = new THREE.Vector3()
const reelCloseFromTarget = new THREE.Vector3()
const ABOUT_CANVAS_SIZE = { width: 1024, height: 640 }
const ABOUT_CLOSE_BOUNDS = { x: 904, y: 24, w: 76, h: 76 }
const ABOUT_IMAGE_BOUNDS = { x: 48, y: 100, w: 430, h: 420 }
const ABOUT_TEXT_BOUNDS = { x: 522, y: 100, w: 430, h: 420 }
const ABOUT_TEXT_STYLE = {
  minFontSize: 14,
  maxFontSize: 21,
  lineHeightRatio: 1.38,
  paragraphGapRatio: 0.65,
  insetX: 24,
  insetY: 24,
  maxCharsDesktop: 50,
  maxCharsMobile: 40
}
const ABOUT_BIO = [
  'Alex Edep is a Los Angeles-based director blending cinematic storytelling with contemporary culture. He has collaborated with artists including Yeat, Drake, and Quavo across music videos, trailers, and visual campaigns.',
  'He wrote and directed My House (2021), with a feature adaptation in development, and is the founder of DUMBDAYS!, a studio dedicated to bold, artist-driven visuals.'
]
const aboutCameraPos = new THREE.Vector3()
let galleryState = 'closed'
let galleryTransitionStart = 0
const GALLERY_TRANSITION_SEC = 0.6
const galleryImages = [
  '/images/gallery/1.webp',
  '/images/gallery/DSC07432-Enhanced-NR.webp',
  '/images/gallery/DSC07604-Enhanced-NR-2.webp',
  '/images/gallery/DSC07639.webp',
  '/images/gallery/DSC08619.webp',
  '/images/gallery/DSCF1752.webp'
]
let galleryIndex = 0
let galleryPlane = null
let galleryPlaneCanvas = null
let galleryPlaneCtx = null
let galleryPlaneTexture = null
let galleryPlaneImage = null
let galleryMode = 'grid'
let galleryThumbRects = []
let galleryThumbs = []
let heroCube = null
let heroCoreMesh = null
let heroCoreGlow = null
let heroDebrisGroup = null
const heroDebris = []
let heroState = 'idle'
let heroTransitionStart = 0
const HERO_TRANSITION_SEC = 0.7
const HERO_TRANSITION_SEC_SHOOT = 1.0
const HERO_RISE = 2.4
const HERO_RISE_MOBILE = 1.8
const HERO_ZOOM_MIN = 0.95
const HERO_ZOOM_MAX = 4.2
const HERO_ZOOM_MIN_MOBILE = 0.85
const HERO_ZOOM_MAX_MOBILE = 3.4
const HERO_CAMERA_OFFSET = new THREE.Vector3(0, 0, 2.0)
const HERO_CAMERA_OFFSET_SHOOT = new THREE.Vector3(0, 0, 1.7)
const HERO_CAMERA_OFFSET_SHOOT_MOBILE = new THREE.Vector3(0, 0, 1.7)
let heroMode = 'dramatic'
const heroBasePos = new THREE.Vector3()
const heroTargetPos = new THREE.Vector3()
const heroCameraFromPosition = new THREE.Vector3()
const heroCameraFromTarget = new THREE.Vector3()
const heroCameraTargetPos = new THREE.Vector3()
const heroCameraLagPos = new THREE.Vector3()
const HERO_CAMERA_LAG = 0.18
const heroSpawnBaseRotation = new THREE.Euler()
const heroSpinStartRot = new THREE.Euler()
const heroSpinTurns = 2.5
const HERO_SPAWN_TILT = new THREE.Euler(0.22, THREE.MathUtils.degToRad(15), -0.06)
let heroClosePlane = null
const heroCloseTarget = new THREE.Vector3()
const HERO_RETURN_OPACITY_START = 0.2
let heroDragActive = false
let heroDragMoved = false
let heroDragLastUp = 0
const heroDragPointer = new THREE.Vector2()
const heroAngularVelocity = new THREE.Vector2()
let heroDragSpeed = 0
let heroLastDragTime = 0
let heroDragDistance = 0
let heroSquishUsedThisDrag = false
let heroPointerDownHit = null
let heroChipClickLockUntil = 0
let heroShakeTime = 0
let heroShakeStrength = 0
let heroShakeActive = false
const heroShakeOffset = new THREE.Vector3()
const heroShakeRot = new THREE.Vector3()
const heroShakeBasePos = new THREE.Vector3()
const heroShakeBaseRot = new THREE.Vector3()
const HERO_SHAKE_DURATION = 0.3
let heroComboCount = 0
let heroLastChipTime = 0
let heroComboDecayUntil = 0
const HERO_COMBO_WINDOW = 1.2
let heroRemovedCells = 0
let heroSquishActive = false
let heroSquishStart = 0
const HERO_SQUISH_DURATION = 1.6
const HERO_SQUISH_AMOUNT = 0.38
let heroSquishAmount = HERO_SQUISH_AMOUNT
let heroSquishWobble = 5
let heroSquishDamp = 2.4
const heroSquishBaseScale = new THREE.Vector3()
let heroSquishCooldownUntil = 0
const HERO_SQUISH_COOLDOWN = 0.12
const heroChipPointLocal = new THREE.Vector3()
const heroChipNormalLocal = new THREE.Vector3(0, 0, 1)
let heroChipMaterialIndex = -1
const HERO_CHIP_SEGMENTS = 20
const HERO_CHIP_RADIUS = 0.17
const HERO_CHIP_DEPTH = 0.035
const HERO_CHIP_MAX = 0.16
const HERO_CHIP_REMOVE = 0.09
const HERO_FLICK_SPEED = 900
const HERO_SPIN_FRICTION = 0.94
const HERO_ROTATION_SPRING = 0.06
const HERO_ROTATION_DAMPING = 0.88
const HERO_DRAG_ROT_SPEED = 0.005
let heroSparkGroup = null
const heroSparks = []
let heroSparkIndex = 0
let ratedModel = null
let ratedRoot = null
let ratedHitMesh = null
let ratedReady = false
let ratedDragActive = false
let ratedDragMoved = false
let ratedDragLastUp = 0
const ratedDragPointer = new THREE.Vector2()
const ratedAngularVelocity = new THREE.Vector2()
let ratedSquishActive = false
let ratedSquishStart = 0
let ratedSquishAmount = 0.25
let ratedSquishWobble = 4.5
let ratedSquishDamp = 2.2
let ratedState = 'idle'
let ratedFocusStart = 0
const RATED_FOCUS_DURATION = 0.7
const ratedFocusFromPosition = new THREE.Vector3()
const ratedFocusFromTarget = new THREE.Vector3()
const ratedFocusFromModelPos = new THREE.Vector3()
const ratedFocusTargetPos = new THREE.Vector3()
const ratedFocusCameraPos = new THREE.Vector3()
const ratedFocusCameraOffset = new THREE.Vector3()
let ratedFocusFromScale = 1
let ratedFocusTargetScale = 1
const ratedSpawnPos = new THREE.Vector3()
const ratedSpawnRot = new THREE.Euler()
const ratedSpawnCameraPos = new THREE.Vector3()
const ratedSpawnForward = new THREE.Vector3()
const ratedSpawnRight = new THREE.Vector3()
const ratedSpawnUp = new THREE.Vector3()
const ratedSpawnTarget = new THREE.Vector3()
const ratedSpawnTilt = new THREE.Euler(0.06, -0.25, 0.04)
let pendingFitText = false
const RATED_SPAWN_DISTANCE = 2.2
const RATED_SPAWN_OFFSET = { x: 0.36, y: -0.3 }
const RATED_SPAWN_OFFSET_MOBILE = { x: 0.4, y: -0.55 }
let ratedSpawnReady = false
let ratedBlockerRadius = 0.28
let ratedLightsReady = false
let ratedAmbientLight = null
let ratedKeyLight = null
let ratedFillLight = null
let heroDonutMesh = null
const heroDonuts = []
const heroDonutDummy = new THREE.Object3D()
let heroHitMesh = null
const HERO_DONUT_COUNT = 60
const HERO_DONUT_RANGE = 6
const HERO_DONUT_CLEAR = 2.0
const aboutHeroPos = new THREE.Vector3()
const aboutPanelPos = new THREE.Vector3()
const aboutCameraTarget = new THREE.Vector3()
const ABOUT_DONUT_COUNT = 40
const ABOUT_DONUT_RANGE = 5
const ABOUT_DONUT_CLEAR = 1.4
const heroFaceDrift = []
let lastTickTime = 0
let videosState = 'closed'
let videosTransitionStart = 0
const VIDEOS_TRANSITION_SEC = 0.6
const videosCloseFromPosition = new THREE.Vector3()
const videosCloseFromTarget = new THREE.Vector3()
const videosTarget = new THREE.Vector3(0, 0, 0)
const videosCameraPosition = new THREE.Vector3(0, 0, 3.1)
let portfolioGroup = null
const portfolioCards = []
let portfolioCloseBtn = null
let videoDetailPlane = null
let videoDetailState = 'closed'
let videoDetailTransitionStart = 0
const VIDEO_DETAIL_TRANSITION_SEC = 0.45
const VIDEO_DETAIL_CLOSE_BOUNDS = { x: 904, y: 24, w: 76, h: 76 }
const videoDetailCloseFromPosition = new THREE.Vector3()
const videoDetailCloseFromTarget = new THREE.Vector3()
const videoPortfolioSections = [
  {
    title: 'Music Videos',
    items: ['Comë N Go', 'The Bell', 'NEWTRIP', 'IM YEAT', 'TMSH', 'CHOP CHOP']
  },
  {
    title: 'Narrative',
    items: ['Coachella', 'Forget Me Not', 'My House']
  },
  {
    title: 'Social Content',
    items: ['The Party', 'Talk to Me', 'Plaqueboymax']
  }
]
const galleryCloseFromPosition = new THREE.Vector3()
const galleryCloseFromTarget = new THREE.Vector3()
const galleryPlaneTarget = new THREE.Vector3(0, 0, 0)
const galleryCameraPosition = new THREE.Vector3(0, 0, 2.4)
const GALLERY_CANVAS_SIZE = { width: 1024, height: 640 }
const GALLERY_IMAGE_BOUNDS = { x: 120, y: 90, w: 784, h: 460 }
const GALLERY_CLOSE_BOUNDS = { x: 904, y: 24, w: 76, h: 76 }
const GALLERY_PREV_BOUNDS = { x: 120, y: 560, w: 160, h: 56 }
const GALLERY_NEXT_BOUNDS = { x: 744, y: 560, w: 160, h: 56 }
const GALLERY_BACK_BOUNDS = { x: 120, y: 24, w: 140, h: 50 }

if (mobileLoader) {
  mobileLoader.style.setProperty('--loader-duration', `${MOBILE_LOADER_MIN_MS}ms`)
}

if (ENABLE_BOOT_LOADER) {
  bodyEl.classList.remove('tap-only')
} else {
  bodyEl.classList.add('tap-only')
  if (tapOverlay && !tapOverlayShown && mobileQuery.matches) {
    tapOverlay.classList.add('is-visible')
    tapOverlayShown = true
  }
}

if (tapOverlay) {
  tapOverlay.addEventListener(
    'pointerdown',
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      lastCanvasClick = performance.now()
      nameClickLockUntil = performance.now() + 1200
      nameInteractEnabled = false
      if (nameMeshRef) {
        nameMeshRef.userData.interactEnabled = false
        nameMeshRef.userData.clickable = false
      }
      startIntroMotion()
      triggerStartupSpin()
      tapOverlay.classList.remove('is-visible')
      window.setTimeout(() => {
        tapOverlay.remove()
      }, 300)
      window.setTimeout(() => {
        enableNameInteraction()
      }, 1000)
    },
    { once: true }
  )
}

if (aboutCloseBtn) {
  aboutCloseBtn.addEventListener('click', () => closeAboutCard())
}
window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeAboutCard()
  if (e.key === 'Escape') closeGallery()
  if (e.key === 'Escape') closeReelCard()
  if (e.key === 'Escape') closeVideosScene()
  if (e.key === 'Escape') closeVideoDetail()
})

function scheduleHideMobileLoader() {
  if (!mobileLoader || !mobileQuery.matches) return
  if (!ENABLE_BOOT_LOADER) return
  const elapsed = performance.now() - loaderStart
  const delay = Math.max(0, MOBILE_LOADER_MIN_MS - elapsed)
  window.setTimeout(() => {
    mobileLoader.classList.add('is-hidden')
    window.setTimeout(() => {
      mobileLoader.remove()
      if (tapOverlay && !tapOverlayShown && mobileQuery.matches) {
        tapOverlay.classList.add('is-visible')
        tapOverlayShown = true
      }
    }, 500)
  }, delay)
}

function tryHideMobileLoader() {
  if (!texturesReady || !fontReady) return
  scheduleHideMobileLoader()
}

function warmupRender(frames = 5) {
  if (!mobileQuery.matches) return
  let remaining = frames
  const renderOnce = () => {
    renderer.render(scene, camera)
    remaining -= 1
    if (remaining > 0) requestAnimationFrame(renderOnce)
  }
  requestAnimationFrame(renderOnce)
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(' ')
  let line = ''
  let currentY = y
  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i] + ' '
    const metrics = ctx.measureText(testLine)
    if (metrics.width > maxWidth && i > 0) {
      ctx.fillText(line.trim(), x, currentY)
      line = words[i] + ' '
      currentY += lineHeight
    } else {
      line = testLine
    }
  }
  if (line) ctx.fillText(line.trim(), x, currentY)
  return currentY + lineHeight
}

function wrapTextLines(ctx, text, maxWidth) {
  const words = text.split(' ')
  const lines = []
  let line = ''
  for (let i = 0; i < words.length; i++) {
    const testLine = line ? `${line} ${words[i]}` : words[i]
    if (ctx.measureText(testLine).width > maxWidth && line) {
      lines.push(line)
      line = words[i]
    } else {
      line = testLine
    }
  }
  if (line) lines.push(line)
  return lines
}

function buildAboutBioWrappedLines(ctx, maxWidth) {
  const lines = []
  for (let p = 0; p < ABOUT_BIO.length; p++) {
    const paragraph = ABOUT_BIO[p].replace(/[–—]/g, '-')
    lines.push(...wrapTextLines(ctx, paragraph, maxWidth))
    if (p < ABOUT_BIO.length - 1) lines.push('')
  }
  return lines
}

function getAboutTextContentBounds() {
  return {
    x: ABOUT_TEXT_BOUNDS.x + ABOUT_TEXT_STYLE.insetX,
    y: ABOUT_TEXT_BOUNDS.y + ABOUT_TEXT_STYLE.insetY,
    w: ABOUT_TEXT_BOUNDS.w - ABOUT_TEXT_STYLE.insetX * 2,
    h: ABOUT_TEXT_BOUNDS.h - ABOUT_TEXT_STYLE.insetY * 2
  }
}

function getAboutBioLayout(ctx) {
  const bounds = getAboutTextContentBounds()
  const isMobile = mobileQuery.matches
  const maxFont = isMobile ? ABOUT_TEXT_STYLE.maxFontSize - 2 : ABOUT_TEXT_STYLE.maxFontSize
  const minFont = isMobile ? ABOUT_TEXT_STYLE.minFontSize : ABOUT_TEXT_STYLE.minFontSize + 1
  for (let fontSize = maxFont; fontSize >= minFont; fontSize -= 1) {
    ctx.font = `${fontSize}px Helvetica, Arial, sans-serif`
    const lineHeight = Math.round(fontSize * ABOUT_TEXT_STYLE.lineHeightRatio)
    const paragraphGap = Math.round(lineHeight * ABOUT_TEXT_STYLE.paragraphGapRatio)
    const lines = buildAboutBioWrappedLines(ctx, bounds.w)
    const totalHeight = lines.reduce((sum, line) => sum + (line ? lineHeight : paragraphGap), 0)
    if (totalHeight <= bounds.h) {
      return { bounds, lines, fontSize, lineHeight, paragraphGap, totalHeight }
    }
  }
  const fallbackFontSize = minFont
  ctx.font = `${fallbackFontSize}px Helvetica, Arial, sans-serif`
  const fallbackLineHeight = Math.round(fallbackFontSize * ABOUT_TEXT_STYLE.lineHeightRatio)
  const fallbackParagraphGap = Math.round(fallbackLineHeight * ABOUT_TEXT_STYLE.paragraphGapRatio)
  const fallbackLines = buildAboutBioWrappedLines(ctx, bounds.w)
  const fallbackHeight = fallbackLines.reduce(
    (sum, line) => sum + (line ? fallbackLineHeight : fallbackParagraphGap),
    0
  )
  return {
    bounds,
    lines: fallbackLines,
    fontSize: fallbackFontSize,
    lineHeight: fallbackLineHeight,
    paragraphGap: fallbackParagraphGap,
    totalHeight: fallbackHeight
  }
}

function drawAboutBioText(ctx) {
  const layout = getAboutBioLayout(ctx)
  ctx.font = `${layout.fontSize}px Helvetica, Arial, sans-serif`
  let y = layout.bounds.y + Math.max(0, (layout.bounds.h - layout.totalHeight) * 0.5)
  ctx.textAlign = 'left'
  ctx.textBaseline = 'top'
  for (const line of layout.lines) {
    if (!line) {
      y += layout.paragraphGap
      continue
    }
    ctx.fillText(line, layout.bounds.x, y)
    y += layout.lineHeight
  }
}

function createTextTexture({
  text,
  width = 512,
  height = 256,
  fontSize = 32,
  fontWeight = 600,
  textColor = '#f2f4f8',
  bgColor = 'rgba(6, 10, 14, 0.88)',
  borderColor = 'rgba(255, 255, 255, 0.1)',
  padding = 24
}) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = bgColor
  ctx.fillRect(0, 0, width, height)
  ctx.strokeStyle = borderColor
  ctx.lineWidth = 2
  ctx.strokeRect(8, 8, width - 16, height - 16)
  ctx.fillStyle = textColor
  ctx.font = `${fontWeight} ${fontSize}px Helvetica, Arial, sans-serif`
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, padding, height / 2)
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  return { texture, canvas }
}

function createCardTexture({ title }) {
  const width = 512
  const height = 384
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = 'rgba(6, 10, 14, 0.9)'
  ctx.fillRect(0, 0, width, height)
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)'
  ctx.lineWidth = 2
  ctx.strokeRect(10, 10, width - 20, height - 20)
  ctx.fillStyle = '#f2f4f8'
  ctx.font = '600 22px Helvetica, Arial, sans-serif'
  ctx.textAlign = 'left'
  ctx.textBaseline = 'bottom'
  ctx.fillText(title, 28, height - 28)
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  return texture
}

function createPortfolioScene() {
  if (portfolioGroup) return
  portfolioGroup = new THREE.Group()
  portfolioGroup.visible = false
  portfolioGroup.position.set(0, 0, 0.2)

  const cols = 3
  const cardW = 0.9
  const cardH = 0.55
  const colGap = 0.22
  const rowGap = 0.28
  const sectionGap = 0.45
  let cursorY = 1.35

  for (const section of videoPortfolioSections) {
    const headerTex = createTextTexture({
      text: section.title,
      width: 512,
      height: 128,
      fontSize: 28,
      bgColor: 'rgba(6, 10, 14, 0.6)'
    }).texture
    const headerMat = new THREE.MeshBasicMaterial({ map: headerTex, transparent: true, depthTest: false })
    const headerMesh = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 0.3), headerMat)
    headerMesh.position.set(0, cursorY, 0)
    headerMesh.renderOrder = 8
    portfolioGroup.add(headerMesh)

    cursorY -= 0.38
    for (let i = 0; i < section.items.length; i++) {
      const col = i % cols
      const row = Math.floor(i / cols)
      const x = (col - (cols - 1) / 2) * (cardW + colGap)
      const y = cursorY - row * (cardH + rowGap)
      const tex = createCardTexture({ title: section.items[i] })
      const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthTest: false })
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(cardW, cardH), mat)
      mesh.position.set(x, y, 0)
      mesh.renderOrder = 7
      mesh.userData.clickable = true
      mesh.userData.project = { title: section.items[i], section: section.title }
      mesh.userData.onClick = () => openVideoDetail(mesh.userData.project)
      portfolioGroup.add(mesh)
      portfolioCards.push(mesh)
      interactive.push(mesh)
    }
    const rows = Math.ceil(section.items.length / cols)
    cursorY -= rows * (cardH + rowGap)
    cursorY -= sectionGap
  }

  const closeTex = createTextTexture({
    text: '×',
    width: 128,
    height: 128,
    fontSize: 48,
    bgColor: 'rgba(6, 10, 14, 0.85)'
  }).texture
  const closeMat = new THREE.MeshBasicMaterial({ map: closeTex, transparent: true, depthTest: false })
  portfolioCloseBtn = new THREE.Mesh(new THREE.PlaneGeometry(0.2, 0.2), closeMat)
  portfolioCloseBtn.position.set(1.45, 1.55, 0.1)
  portfolioCloseBtn.renderOrder = 9
  portfolioCloseBtn.userData.clickable = true
  portfolioCloseBtn.userData.onClick = () => closeVideosScene()
  portfolioGroup.add(portfolioCloseBtn)
  interactive.push(portfolioCloseBtn)

  scene.add(portfolioGroup)
}

function createVideoDetailPlane({ title, section }) {
  if (videoDetailPlane) return
  const canvas = document.createElement('canvas')
  canvas.width = 1024
  canvas.height = 640
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = 'rgba(6, 10, 14, 0.96)'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)'
  ctx.lineWidth = 2
  ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20)
  ctx.fillStyle = '#f2f4f8'
  ctx.font = '600 32px Helvetica, Arial, sans-serif'
  ctx.fillText(title, 60, 120)
  ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
  ctx.font = '18px Helvetica, Arial, sans-serif'
  ctx.fillText(section, 60, 160)
  ctx.fillStyle = 'rgba(255, 255, 255, 0.75)'
  ctx.font = '16px Helvetica, Arial, sans-serif'
  ctx.fillText('Details coming soon.', 60, 230)
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(VIDEO_DETAIL_CLOSE_BOUNDS.x + VIDEO_DETAIL_CLOSE_BOUNDS.w / 2, VIDEO_DETAIL_CLOSE_BOUNDS.y + VIDEO_DETAIL_CLOSE_BOUNDS.h / 2, 26, 0, Math.PI * 2)
  ctx.stroke()
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)'
  ctx.font = '22px Helvetica, Arial, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('×', VIDEO_DETAIL_CLOSE_BOUNDS.x + VIDEO_DETAIL_CLOSE_BOUNDS.w / 2, VIDEO_DETAIL_CLOSE_BOUNDS.y + VIDEO_DETAIL_CLOSE_BOUNDS.h / 2)

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  const aspect = canvas.width / canvas.height
  const geometry = new THREE.PlaneGeometry(aspect * 1.1, 1.1)
  const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true, depthTest: false, depthWrite: false })
  videoDetailPlane = new THREE.Mesh(geometry, material)
  videoDetailPlane.renderOrder = 11
  videoDetailPlane.visible = false
  scene.add(videoDetailPlane)
}
function ensureAboutImage() {
  if (aboutPlaneImage) return
  aboutPlaneImage = new Image()
  aboutPlaneImage.src = '/images/self_1.png'
  aboutPlaneImage.onload = () => {
    drawAboutCardTexture()
    drawAboutPanelTexture()
    drawAboutPanelContentTexture()
    updateAboutPanelImagePlane()
  }
}

function drawAboutCardTexture() {
  if (!aboutPlaneCtx || !aboutPlaneCanvas) return
  const { width, height } = ABOUT_CANVAS_SIZE
  const ctx = aboutPlaneCtx
  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = 'rgba(6, 10, 14, 0.96)'
  ctx.fillRect(0, 0, width, height)

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)'
  ctx.lineWidth = 2
  ctx.strokeRect(10, 10, width - 20, height - 20)

  if (aboutPlaneImage && aboutPlaneImage.complete) {
    ctx.save()
    ctx.beginPath()
    ctx.rect(ABOUT_IMAGE_BOUNDS.x, ABOUT_IMAGE_BOUNDS.y, ABOUT_IMAGE_BOUNDS.w, ABOUT_IMAGE_BOUNDS.h)
    ctx.clip()
    drawContainImage(ctx, aboutPlaneImage, ABOUT_IMAGE_BOUNDS)
    ctx.restore()
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)'
    ctx.strokeRect(ABOUT_IMAGE_BOUNDS.x, ABOUT_IMAGE_BOUNDS.y, ABOUT_IMAGE_BOUNDS.w, ABOUT_IMAGE_BOUNDS.h)
  } else {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.08)'
    ctx.fillRect(ABOUT_IMAGE_BOUNDS.x, ABOUT_IMAGE_BOUNDS.y, ABOUT_IMAGE_BOUNDS.w, ABOUT_IMAGE_BOUNDS.h)
  }

  ctx.fillStyle = 'rgba(255, 255, 255, 0.78)'
  drawAboutBioText(ctx)

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(ABOUT_CLOSE_BOUNDS.x + ABOUT_CLOSE_BOUNDS.w / 2, ABOUT_CLOSE_BOUNDS.y + ABOUT_CLOSE_BOUNDS.h / 2, 26, 0, Math.PI * 2)
  ctx.stroke()
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)'
  ctx.font = '22px Helvetica, Arial, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('×', ABOUT_CLOSE_BOUNDS.x + ABOUT_CLOSE_BOUNDS.w / 2, ABOUT_CLOSE_BOUNDS.y + ABOUT_CLOSE_BOUNDS.h / 2)
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'

  if (aboutPlaneTexture) aboutPlaneTexture.needsUpdate = true
}

function drawContainImage(ctx, image, rect) {
  if (!image || !image.complete || !image.naturalWidth) return
  const imgAspect = image.naturalWidth / image.naturalHeight
  const rectAspect = rect.w / rect.h
  let drawW = rect.w
  let drawH = rect.h
  if (imgAspect > rectAspect) {
    drawW = rect.w
    drawH = rect.w / imgAspect
  } else {
    drawH = rect.h
    drawW = rect.h * imgAspect
  }
  const dx = rect.x + (rect.w - drawW) / 2
  const dy = rect.y + (rect.h - drawH) / 2
  ctx.drawImage(image, dx, dy, drawW, drawH)
}

function aboutCanvasToLocal(x, y, panelW, panelH) {
  return new THREE.Vector2(
    (x / ABOUT_CANVAS_SIZE.width - 0.5) * panelW,
    (0.5 - y / ABOUT_CANVAS_SIZE.height) * panelH
  )
}

function buildAboutBioLines(maxChars = 62) {
  const lines = []
  for (let p = 0; p < ABOUT_BIO.length; p++) {
    const paragraph = ABOUT_BIO[p].replace(/[–—]/g, '-')
    const words = paragraph.split(/\s+/).filter(Boolean)
    let line = ''
    for (const word of words) {
      const next = line ? `${line} ${word}` : word
      if (next.length > maxChars && line) {
        lines.push(line)
        line = word
      } else {
        line = next
      }
    }
    if (line) lines.push(line)
    if (p < ABOUT_BIO.length - 1) lines.push('')
  }
  return lines
}

function destroyAboutTextMeshes() {
  if (!aboutPanelTextGroup || !aboutPanelPlane) return
  aboutPanelPlane.remove(aboutPanelTextGroup)
  aboutPanelTextGroup.traverse((child) => {
    if (!child.isMesh) return
    if (child.geometry) child.geometry.dispose()
    if (child.material) child.material.dispose()
  })
  aboutPanelTextGroup = null
}

function updateAboutPanelImagePlane() {
  if (!aboutPanelImagePlane || !aboutPanelPlane?.userData?.panelSize) return
  const { panelW, panelH } = aboutPanelPlane.userData.panelSize
  const maxW = (ABOUT_IMAGE_BOUNDS.w / ABOUT_CANVAS_SIZE.width) * panelW
  const maxH = (ABOUT_IMAGE_BOUNDS.h / ABOUT_CANVAS_SIZE.height) * panelH
  const aspect = (aboutPlaneImage?.naturalWidth && aboutPlaneImage?.naturalHeight)
    ? aboutPlaneImage.naturalWidth / aboutPlaneImage.naturalHeight
    : ABOUT_IMAGE_BOUNDS.w / ABOUT_IMAGE_BOUNDS.h
  let drawW = maxW
  let drawH = drawW / aspect
  if (drawH > maxH) {
    drawH = maxH
    drawW = drawH * aspect
  }
  if (aboutPanelImagePlane.geometry) aboutPanelImagePlane.geometry.dispose()
  aboutPanelImagePlane.geometry = new THREE.PlaneGeometry(drawW, drawH)
}

function createAboutTextMeshes() {
  if (!aboutPanelPlane || !aboutPanelPlane.userData?.panelSize || !aboutPrefer3DText) {
    aboutText3DEnabled = false
    destroyAboutTextMeshes()
    drawAboutPanelContentTexture()
    return
  }
  if (!uiFont) return
  destroyAboutTextMeshes()
  try {
    const { panelW, panelH } = aboutPanelPlane.userData.panelSize
    const layoutCtx = aboutPanelContentCtx || document.createElement('canvas').getContext('2d')
    if (!layoutCtx) return
    const layout = getAboutBioLayout(layoutCtx)
    const unitsPerPx = panelH / ABOUT_CANVAS_SIZE.height
    const sizePx = layout.fontSize
    const depthPx = mobileQuery.matches ? 6 : 8
    const lineHeight = layout.lineHeight
    const size = unitsPerPx * sizePx
    const depth = unitsPerPx * depthPx
    const paragraphGap = layout.paragraphGap
    let y = layout.bounds.y + Math.max(0, (layout.bounds.h - layout.totalHeight) * 0.5)
    aboutPanelTextGroup = new THREE.Group()
    aboutPanelTextGroup.position.z = 0.02
    aboutPanelTextGroup.renderOrder = 12
    for (const line of layout.lines) {
      if (!line) {
        y += paragraphGap
        continue
      }
      const geo = new TextGeometry(line, {
        font: uiFont,
        size,
        depth,
        curveSegments: 8,
        bevelEnabled: true,
        bevelThickness: depth * 0.18,
        bevelSize: depth * 0.14,
        bevelOffset: 0,
        bevelSegments: 2
      })
      geo.computeBoundingBox()
      const minX = geo.boundingBox?.min?.x ?? 0
      geo.translate(-minX, 0, 0)
      const frontMat = new THREE.MeshBasicMaterial({ color: 0xf2f4f8, transparent: true, opacity: 1 })
      const sideMat = new THREE.MeshBasicMaterial({ color: 0xaeb3bf, transparent: true, opacity: 1 })
      const mesh = new THREE.Mesh(geo, [frontMat, sideMat])
      const local = aboutCanvasToLocal(layout.bounds.x, y, panelW, panelH)
      mesh.position.set(local.x, local.y, 0)
      aboutPanelTextGroup.add(mesh)
      y += lineHeight
    }
    aboutPanelPlane.add(aboutPanelTextGroup)
    aboutText3DEnabled = true
  } catch (err) {
    aboutText3DEnabled = false
    destroyAboutTextMeshes()
    console.warn('About 3D text disabled due to geometry error:', err)
  }
  drawAboutPanelContentTexture()
}

function setAboutTextOpacity(opacity) {
  if (!aboutPanelTextGroup) return
  aboutPanelTextGroup.traverse((child) => {
    if (!child.isMesh || !child.material) return
    child.material.transparent = true
    child.material.opacity = opacity
  })
}

function setAbout3DTextEnabled(enabled) {
  aboutPrefer3DText = Boolean(enabled)
  if (!aboutPrefer3DText) {
    aboutText3DEnabled = false
    destroyAboutTextMeshes()
    drawAboutPanelContentTexture()
    return false
  }
  createAboutTextMeshes()
  drawAboutPanelContentTexture()
  return aboutText3DEnabled
}

function getReelBounds() {
  const { width, height } = REEL_CANVAS_SIZE
  const padding = Math.max(18, Math.round(width * 0.02))
  const closeSize = Math.max(44, Math.round(width * 0.06))
  return {
    image: { x: 0, y: 0, w: width, h: height },
    close: { x: width - padding - closeSize, y: padding, w: closeSize, h: closeSize }
  }
}

function drawReelCardTexture() {
  if (!reelPlaneCtx || !reelPlaneCanvas) return
  const { width, height } = REEL_CANVAS_SIZE
  const bounds = getReelBounds()
  const ctx = reelPlaneCtx
  ctx.clearRect(0, 0, width, height)

  if (reelPlaneImage && reelImageReady && reelPlaneImage.complete) {
    ctx.save()
    ctx.beginPath()
    ctx.rect(bounds.image.x, bounds.image.y, bounds.image.w, bounds.image.h)
    ctx.clip()
    drawContainImage(ctx, reelPlaneImage, bounds.image)
    ctx.restore()
  } else {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.08)'
    ctx.fillRect(bounds.image.x, bounds.image.y, bounds.image.w, bounds.image.h)
  }

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(bounds.close.x + bounds.close.w / 2, bounds.close.y + bounds.close.h / 2, bounds.close.w / 2, 0, Math.PI * 2)
  ctx.stroke()
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)'
  ctx.font = '22px Helvetica, Arial, sans-serif'
  ctx.fillText('×', bounds.close.x + bounds.close.w / 2, bounds.close.y + bounds.close.h / 2)
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'

  if (reelPlaneTexture) reelPlaneTexture.needsUpdate = true
}

function createAboutPlane() {
  if (aboutPlane) return
  ensureAboutImage()
  aboutPlaneCanvas = document.createElement('canvas')
  aboutPlaneCanvas.width = ABOUT_CANVAS_SIZE.width
  aboutPlaneCanvas.height = ABOUT_CANVAS_SIZE.height
  aboutPlaneCtx = aboutPlaneCanvas.getContext('2d')
  aboutPlaneTexture = new THREE.CanvasTexture(aboutPlaneCanvas)
  aboutPlaneTexture.colorSpace = THREE.SRGBColorSpace

  drawAboutCardTexture()
  const aspect = ABOUT_CANVAS_SIZE.width / ABOUT_CANVAS_SIZE.height
  const geometry = new THREE.PlaneGeometry(aspect * 1.4, 1.4)
  const material = new THREE.MeshBasicMaterial({
    map: aboutPlaneTexture,
    transparent: true,
    depthTest: false,
    depthWrite: false
  })
  aboutPlane = new THREE.Mesh(geometry, material)
  aboutPlane.renderOrder = 10
  aboutPlane.visible = false
  scene.add(aboutPlane)
}

function drawAboutPanelTexture() {
  if (!aboutPanelCtx || !aboutPanelCanvas) return
  const width = aboutPanelCanvas.width
  const height = aboutPanelCanvas.height
  const ctx = aboutPanelCtx
  ctx.clearRect(0, 0, width, height)

  if (aboutPanelTexture) aboutPanelTexture.needsUpdate = true
}

function drawAboutPanelContentTexture() {
  if (!aboutPanelContentCtx || !aboutPanelContentCanvas) return
  const ctx = aboutPanelContentCtx
  ctx.clearRect(0, 0, aboutPanelContentCanvas.width, aboutPanelContentCanvas.height)

  if (!aboutText3DEnabled) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.78)'
    drawAboutBioText(ctx)
  }

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(ABOUT_CLOSE_BOUNDS.x + ABOUT_CLOSE_BOUNDS.w / 2, ABOUT_CLOSE_BOUNDS.y + ABOUT_CLOSE_BOUNDS.h / 2, 26, 0, Math.PI * 2)
  ctx.stroke()
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)'
  ctx.font = '22px Helvetica, Arial, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('×', ABOUT_CLOSE_BOUNDS.x + ABOUT_CLOSE_BOUNDS.w / 2, ABOUT_CLOSE_BOUNDS.y + ABOUT_CLOSE_BOUNDS.h / 2)
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'

  if (aboutPanelContentTexture) aboutPanelContentTexture.needsUpdate = true
}

function createAboutPanelPlane() {
  if (aboutPanelPlane) return
  ensureAboutImage()
  aboutPanelCanvas = document.createElement('canvas')
  aboutPanelCanvas.width = ABOUT_CANVAS_SIZE.width
  aboutPanelCanvas.height = ABOUT_CANVAS_SIZE.height
  aboutPanelCtx = aboutPanelCanvas.getContext('2d')
  aboutPanelTexture = new THREE.CanvasTexture(aboutPanelCanvas)
  aboutPanelTexture.colorSpace = THREE.SRGBColorSpace
  aboutPanelContentCanvas = document.createElement('canvas')
  aboutPanelContentCanvas.width = ABOUT_CANVAS_SIZE.width
  aboutPanelContentCanvas.height = ABOUT_CANVAS_SIZE.height
  aboutPanelContentCtx = aboutPanelContentCanvas.getContext('2d')
  aboutPanelContentTexture = new THREE.CanvasTexture(aboutPanelContentCanvas)
  aboutPanelContentTexture.colorSpace = THREE.SRGBColorSpace
  drawAboutPanelTexture()
  drawAboutPanelContentTexture()
  const aspect = aboutPanelCanvas.width / aboutPanelCanvas.height
  const panelH = 1.05
  const panelW = aspect * panelH
  const geometry = new THREE.PlaneGeometry(panelW, panelH)
  const material = new THREE.MeshBasicMaterial({
    map: aboutPanelTexture,
    transparent: true,
    depthTest: false,
    depthWrite: false
  })
  aboutPanelPlane = new THREE.Mesh(geometry, material)
  aboutPanelPlane.userData.panelSize = { panelW, panelH }
  aboutPanelPlane.renderOrder = 10
  const backCanvas = document.createElement('canvas')
  backCanvas.width = ABOUT_CANVAS_SIZE.width
  backCanvas.height = ABOUT_CANVAS_SIZE.height
  const backCtx = backCanvas.getContext('2d')
  backCtx.clearRect(0, 0, backCanvas.width, backCanvas.height)
  const cardX = ABOUT_TEXT_BOUNDS.x
  const cardY = ABOUT_TEXT_BOUNDS.y
  const cardW = ABOUT_TEXT_BOUNDS.w
  const cardH = ABOUT_TEXT_BOUNDS.h
  backCtx.fillStyle = 'rgba(6, 10, 14, 0.9)'
  backCtx.fillRect(cardX, cardY, cardW, cardH)
  backCtx.strokeStyle = 'rgba(255, 255, 255, 0.12)'
  backCtx.lineWidth = 2
  backCtx.strokeRect(cardX, cardY, cardW, cardH)
  const backTexture = new THREE.CanvasTexture(backCanvas)
  backTexture.colorSpace = THREE.SRGBColorSpace
  const backMaterial = new THREE.MeshBasicMaterial({
    map: backTexture,
    transparent: true,
    opacity: 0.9,
    depthTest: false,
    depthWrite: false
  })
  aboutPanelBackPlane = new THREE.Mesh(new THREE.PlaneGeometry(aspect * 1.08, 1.08), backMaterial)
  aboutPanelBackPlane.position.set(0, 0, 0)
  aboutPanelBackPlane.rotation.set(0, 0, 0)
  aboutPanelBackPlane.renderOrder = 9
  aboutPanelPlane.add(aboutPanelBackPlane)
  const imageTexture = heroTexture.clone()
  imageTexture.colorSpace = THREE.SRGBColorSpace
  imageTexture.wrapS = THREE.ClampToEdgeWrapping
  imageTexture.wrapT = THREE.ClampToEdgeWrapping
  const imageMaterial = new THREE.MeshBasicMaterial({
    map: imageTexture,
    transparent: true,
    depthTest: false,
    depthWrite: false
  })
  aboutPanelImagePlane = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), imageMaterial)
  const imageCenter = aboutCanvasToLocal(
    ABOUT_IMAGE_BOUNDS.x + ABOUT_IMAGE_BOUNDS.w * 0.5,
    ABOUT_IMAGE_BOUNDS.y + ABOUT_IMAGE_BOUNDS.h * 0.5,
    panelW,
    panelH
  )
  aboutPanelImagePlane.position.set(imageCenter.x, imageCenter.y, 0.002)
  aboutPanelImagePlane.renderOrder = 10
  aboutPanelPlane.add(aboutPanelImagePlane)
  updateAboutPanelImagePlane()
  const contentMaterial = new THREE.MeshBasicMaterial({
    map: aboutPanelContentTexture,
    transparent: true,
    depthTest: false,
    depthWrite: false
  })
  aboutPanelContentPlane = new THREE.Mesh(new THREE.PlaneGeometry(aspect * 1.03, 1.03), contentMaterial)
  aboutPanelContentPlane.position.set(0, 0, 0.004)
  aboutPanelContentPlane.renderOrder = 11
  aboutPanelPlane.add(aboutPanelContentPlane)
  aboutPanelPlane.visible = false
  scene.add(aboutPanelPlane)
}

createAboutPanelPlane()

function createAboutDonuts() {
  if (aboutDonutMesh) return
  const donutGeometry = new THREE.BoxGeometry(0.3, 0.3, 0.3)
  aboutDonutMesh = new THREE.InstancedMesh(donutGeometry, donutBaseMaterial.clone(), ABOUT_DONUT_COUNT)
  aboutDonutMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
  let tries = 0
  const maxTries = ABOUT_DONUT_COUNT * 200
  while (aboutDonuts.length < ABOUT_DONUT_COUNT && tries < maxTries) {
    tries++
    const pos = new THREE.Vector3(
      (Math.random() - 0.5) * ABOUT_DONUT_RANGE,
      (Math.random() - 0.5) * ABOUT_DONUT_RANGE,
      (Math.random() - 0.5) * ABOUT_DONUT_RANGE - 0.6
    )
    if (pos.length() < ABOUT_DONUT_CLEAR) continue
    if (pos.z > -0.3) continue
    const rotation = new THREE.Euler(Math.random() * Math.PI, Math.random() * Math.PI, 0)
    const s = THREE.MathUtils.randFloat(0.4, 0.9)
    const scale = new THREE.Vector3(s, s, s)
    aboutDonuts.push({
      basePos: pos.clone(),
      pos: pos.clone(),
      rotation,
      rotSpeed: THREE.MathUtils.randFloat(0.2, 0.6),
      scale: scale.clone()
    })
  }
  for (let i = 0; i < aboutDonuts.length; i++) {
    const donut = aboutDonuts[i]
    aboutDonutDummy.position.copy(donut.basePos)
    aboutDonutDummy.rotation.copy(donut.rotation)
    aboutDonutDummy.scale.copy(donut.scale)
    aboutDonutDummy.updateMatrix()
    aboutDonutMesh.setMatrixAt(i, aboutDonutDummy.matrix)
  }
  aboutDonutMesh.count = aboutDonuts.length
  aboutDonutMesh.instanceMatrix.needsUpdate = true
  aboutDonutMesh.visible = false
  scene.add(aboutDonutMesh)
}

function createReelPlane() {
  if (reelPlane) return
  reelPlaneCanvas = document.createElement('canvas')
  reelPlaneCanvas.width = REEL_CANVAS_SIZE.width
  reelPlaneCanvas.height = REEL_CANVAS_SIZE.height
  reelPlaneCtx = reelPlaneCanvas.getContext('2d')
  reelPlaneTexture = new THREE.CanvasTexture(reelPlaneCanvas)
  reelPlaneTexture.colorSpace = THREE.SRGBColorSpace

  reelPlaneImage = new Image()
  reelPlaneImage.crossOrigin = 'anonymous'
  reelPlaneImage.onload = () => {
    reelImageReady = true
    if (reelPlaneImage.naturalWidth && reelPlaneImage.naturalHeight) {
      const aspect = reelPlaneImage.naturalWidth / reelPlaneImage.naturalHeight
      REEL_CANVAS_SIZE.width = 1024
      REEL_CANVAS_SIZE.height = Math.round(1024 / aspect)
      reelPlaneCanvas.width = REEL_CANVAS_SIZE.width
      reelPlaneCanvas.height = REEL_CANVAS_SIZE.height
      reelPlaneTexture.needsUpdate = true
      if (reelPlane) reelPlane.scale.set(aspect, 1, 1)
    }
    drawReelCardTexture()
  }
  reelPlaneImage.onerror = () => {
    reelImageReady = false
    drawReelCardTexture()
  }
  reelPlaneImage.src = REEL_THUMB_URL

  drawReelCardTexture()
  const geometry = new THREE.PlaneGeometry(REEL_BASE_SIZE.width, REEL_BASE_SIZE.height)
  const material = new THREE.MeshBasicMaterial({
    map: reelPlaneTexture,
    transparent: true,
    depthTest: false,
    depthWrite: false
  })
  reelPlane = new THREE.Mesh(geometry, material)
  reelPlane.scale.set(REEL_CANVAS_SIZE.width / REEL_CANVAS_SIZE.height, 1, 1)
  reelPlane.renderOrder = 10
  reelPlane.visible = false
  scene.add(reelPlane)
}

function createHeroClosePlane() {
  if (heroClosePlane) return
  const canvas = document.createElement('canvas')
  canvas.width = 128
  canvas.height = 128
  const ctx = canvas.getContext('2d')
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = '#f2f4f8'
  ctx.font = '600 36px Helvetica, Arial, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('×', canvas.width / 2, canvas.height / 2)
  const closeTex = new THREE.CanvasTexture(canvas)
  closeTex.colorSpace = THREE.SRGBColorSpace
  const closeMat = new THREE.MeshBasicMaterial({ map: closeTex, transparent: true, depthTest: false })
  heroClosePlane = new THREE.Mesh(new THREE.PlaneGeometry(0.14, 0.14), closeMat)
  heroClosePlane.renderOrder = 12
  heroClosePlane.userData.clickable = true
  heroClosePlane.userData.onClick = () => closeHeroCube()
  camera.add(heroClosePlane)
  heroClosePlane.position.set(0.92, 0.62, -1.2)
  heroClosePlane.scale.setScalar(mobileQuery.matches ? 0.85 : 1)
  heroClosePlane.visible = false
  interactive.push(heroClosePlane)
}

function captureControlsDefaults() {
  controlsDefaults.enableRotate = controls.enableRotate
  controlsDefaults.enableZoom = controls.enableZoom
  controlsDefaults.enablePan = controls.enablePan
  controlsDefaults.minDistance = controls.minDistance
  controlsDefaults.maxDistance = controls.maxDistance
  controlsDefaults.minAzimuthAngle = controls.minAzimuthAngle
  controlsDefaults.maxAzimuthAngle = controls.maxAzimuthAngle
  controlsDefaults.minPolarAngle = controls.minPolarAngle
  controlsDefaults.maxPolarAngle = controls.maxPolarAngle
  controlsDefaults.target.copy(controls.target)
  controlsDefaults.position.copy(camera.position)
}

function applyAboutControls() {
  controls.enablePan = false
  controls.enableZoom = false
  controls.enableRotate = true
  controls.minAzimuthAngle = -Math.PI / 2
  controls.maxAzimuthAngle = Math.PI / 2
  controls.minPolarAngle = Math.PI / 2 - 0.2
  controls.maxPolarAngle = Math.PI / 2 + 0.2
}

function restoreControls() {
  controls.enablePan = controlsDefaults.enablePan
  controls.enableZoom = controlsDefaults.enableZoom
  controls.enableRotate = controlsDefaults.enableRotate
  controls.minDistance = controlsDefaults.minDistance
  controls.maxDistance = controlsDefaults.maxDistance
  controls.minAzimuthAngle = controlsDefaults.minAzimuthAngle
  controls.maxAzimuthAngle = controlsDefaults.maxAzimuthAngle
  controls.minPolarAngle = controlsDefaults.minPolarAngle
  controls.maxPolarAngle = controlsDefaults.maxPolarAngle
  controls.target.copy(controlsDefaults.target)
  camera.position.copy(controlsDefaults.position)
}

function openAboutCard() {
  if (aboutState !== 'closed') return
  if (introActive) {
    introActive = false
  }
  createAboutPanelPlane()
  captureControlsDefaults()
  aboutState = 'opening'
  aboutTransitionStart = clock.getElapsedTime()
  if (aboutCard) {
    aboutCard.classList.remove('is-open')
    aboutCard.setAttribute('aria-hidden', 'true')
  }
  bodyEl.classList.add('about-open')
  if (aboutPanelPlane) {
    aboutPanelPlane.visible = true
    if (aboutPanelPlane.parent === camera) {
      camera.remove(aboutPanelPlane)
      scene.add(aboutPanelPlane)
    }
    aboutPanelPlane.position.copy(controls.target)
    aboutPanelPlane.position.z += 0.15
    aboutPanelPlane.updateMatrixWorld(true)
    aboutPanelPlane.userData.baseScale = aboutPanelPlane.scale.clone()
    if (aboutPanelPlane.material) aboutPanelPlane.material.opacity = 1
    if (aboutPanelBackPlane?.material) aboutPanelBackPlane.material.opacity = 0.9
    if (aboutPanelImagePlane?.material) aboutPanelImagePlane.material.opacity = 1
    if (aboutPanelContentPlane?.material) aboutPanelContentPlane.material.opacity = 1
    aboutCameraTarget.copy(aboutPanelPlane.position)
    const panelBox = new THREE.Box3().setFromObject(aboutPanelPlane)
    const panelSize = new THREE.Vector3()
    panelBox.getSize(panelSize)
    const padding = mobileQuery.matches ? 1.45 : 1.7
    const fovRad = THREE.MathUtils.degToRad(camera.fov)
    const distance = (panelSize.x * padding) / (2 * Math.tan(fovRad / 2) * camera.aspect)
    const camDir = camera.position.clone().sub(controls.target).normalize()
    aboutCameraPos.copy(aboutCameraTarget).add(camDir.multiplyScalar(distance))
  }
  applyAboutControls()
}

function closeAboutCard() {
  if (aboutState === 'closed' || aboutState === 'closing') return
  aboutState = 'closing'
  aboutTransitionStart = clock.getElapsedTime()
  aboutCloseFromPosition.copy(camera.position)
  aboutCloseFromTarget.copy(controls.target)
  if (aboutCard) {
    aboutCard.classList.remove('is-open')
    aboutCard.setAttribute('aria-hidden', 'true')
  }
  bodyEl.classList.remove('about-open')
}

function applyReelControls() {
  controls.enablePan = false
  controls.enableZoom = false
  controls.enableRotate = true
  controls.minAzimuthAngle = -0.4
  controls.maxAzimuthAngle = 0.4
  controls.minPolarAngle = Math.PI / 2 - 0.25
  controls.maxPolarAngle = Math.PI / 2 + 0.25
}

function openReelCard() {
  if (reelState !== 'closed') return
  createReelPlane()
  captureControlsDefaults()
  reelState = 'opening'
  reelTransitionStart = clock.getElapsedTime()
  reelPlane.visible = true
  reelPlane.position.copy(REEL_TARGET)
  reelPlane.position.z = 0.55
  applyReelControls()
}

function closeReelCard() {
  if (reelState === 'closed' || reelState === 'closing') return
  reelState = 'closing'
  reelTransitionStart = clock.getElapsedTime()
  reelCloseFromPosition.copy(camera.position)
  reelCloseFromTarget.copy(controls.target)
}

function createGalleryPlane() {
  if (galleryPlane) return
  galleryPlaneCanvas = document.createElement('canvas')
  galleryPlaneCanvas.width = GALLERY_CANVAS_SIZE.width
  galleryPlaneCanvas.height = GALLERY_CANVAS_SIZE.height
  galleryPlaneCtx = galleryPlaneCanvas.getContext('2d')
  galleryPlaneTexture = new THREE.CanvasTexture(galleryPlaneCanvas)
  galleryPlaneTexture.colorSpace = THREE.SRGBColorSpace

  const aspect = GALLERY_CANVAS_SIZE.width / GALLERY_CANVAS_SIZE.height
  const geometry = new THREE.PlaneGeometry(aspect * 1.4, 1.4)
  const material = new THREE.MeshBasicMaterial({
    map: galleryPlaneTexture,
    transparent: true,
    depthTest: false,
    depthWrite: false
  })
  galleryPlane = new THREE.Mesh(geometry, material)
  galleryPlane.renderOrder = 9
  galleryPlane.visible = false
  scene.add(galleryPlane)
  preloadGalleryThumbs()
}

function preloadGalleryThumbs() {
  if (galleryThumbs.length) return
  galleryThumbs = galleryImages.map((url) => {
    const img = new Image()
    img.src = url
    img.onload = () => drawGalleryTexture()
    return img
  })
}

function drawCoverImage(ctx, image, rect) {
  if (!image || !image.complete || !image.naturalWidth) return
  const imgAspect = image.naturalWidth / image.naturalHeight
  const rectAspect = rect.w / rect.h
  let drawW = rect.w
  let drawH = rect.h
  if (imgAspect > rectAspect) {
    drawH = rect.h
    drawW = rect.h * imgAspect
  } else {
    drawW = rect.w
    drawH = rect.w / imgAspect
  }
  const dx = rect.x + (rect.w - drawW) / 2
  const dy = rect.y + (rect.h - drawH) / 2
  ctx.drawImage(image, dx, dy, drawW, drawH)
}

function drawGalleryTexture() {
  if (!galleryPlaneCtx || !galleryPlaneCanvas) return
  const { width, height } = GALLERY_CANVAS_SIZE
  const ctx = galleryPlaneCtx
  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = 'rgba(6, 10, 14, 0.96)'
  ctx.fillRect(0, 0, width, height)
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)'
  ctx.lineWidth = 2
  ctx.strokeRect(10, 10, width - 20, height - 20)

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(GALLERY_CLOSE_BOUNDS.x + GALLERY_CLOSE_BOUNDS.w / 2, GALLERY_CLOSE_BOUNDS.y + GALLERY_CLOSE_BOUNDS.h / 2, 26, 0, Math.PI * 2)
  ctx.stroke()
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)'
  ctx.font = '22px Helvetica, Arial, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('×', GALLERY_CLOSE_BOUNDS.x + GALLERY_CLOSE_BOUNDS.w / 2, GALLERY_CLOSE_BOUNDS.y + GALLERY_CLOSE_BOUNDS.h / 2)

  if (galleryMode === 'single') {
    if (galleryPlaneImage && galleryPlaneImage.complete) {
      ctx.save()
      ctx.beginPath()
      ctx.rect(GALLERY_IMAGE_BOUNDS.x, GALLERY_IMAGE_BOUNDS.y, GALLERY_IMAGE_BOUNDS.w, GALLERY_IMAGE_BOUNDS.h)
      ctx.clip()
      ctx.drawImage(
        galleryPlaneImage,
        GALLERY_IMAGE_BOUNDS.x,
        GALLERY_IMAGE_BOUNDS.y,
        GALLERY_IMAGE_BOUNDS.w,
        GALLERY_IMAGE_BOUNDS.h
      )
      ctx.restore()
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)'
      ctx.strokeRect(GALLERY_IMAGE_BOUNDS.x, GALLERY_IMAGE_BOUNDS.y, GALLERY_IMAGE_BOUNDS.w, GALLERY_IMAGE_BOUNDS.h)
    } else {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.08)'
      ctx.fillRect(GALLERY_IMAGE_BOUNDS.x, GALLERY_IMAGE_BOUNDS.y, GALLERY_IMAGE_BOUNDS.w, GALLERY_IMAGE_BOUNDS.h)
    }

    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
    ctx.font = '14px Helvetica, Arial, sans-serif'
    ctx.fillText(`${galleryIndex + 1} / ${galleryImages.length}`, 470, 588)

    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)'
    ctx.fillRect(GALLERY_PREV_BOUNDS.x, GALLERY_PREV_BOUNDS.y, GALLERY_PREV_BOUNDS.w, GALLERY_PREV_BOUNDS.h)
    ctx.fillRect(GALLERY_NEXT_BOUNDS.x, GALLERY_NEXT_BOUNDS.y, GALLERY_NEXT_BOUNDS.w, GALLERY_NEXT_BOUNDS.h)
    ctx.fillStyle = '#0b0e12'
    ctx.font = '600 16px Helvetica, Arial, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('Prev', GALLERY_PREV_BOUNDS.x + GALLERY_PREV_BOUNDS.w / 2, GALLERY_PREV_BOUNDS.y + GALLERY_PREV_BOUNDS.h / 2)
    ctx.fillText('Next', GALLERY_NEXT_BOUNDS.x + GALLERY_NEXT_BOUNDS.w / 2, GALLERY_NEXT_BOUNDS.y + GALLERY_NEXT_BOUNDS.h / 2)

    ctx.fillStyle = 'rgba(255, 255, 255, 0.18)'
    ctx.fillRect(GALLERY_BACK_BOUNDS.x, GALLERY_BACK_BOUNDS.y, GALLERY_BACK_BOUNDS.w, GALLERY_BACK_BOUNDS.h)
    ctx.fillStyle = '#f2f4f8'
    ctx.font = '600 14px Helvetica, Arial, sans-serif'
    ctx.fillText('Back', GALLERY_BACK_BOUNDS.x + GALLERY_BACK_BOUNDS.w / 2, GALLERY_BACK_BOUNDS.y + GALLERY_BACK_BOUNDS.h / 2)
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
  } else {
    galleryThumbRects = []
    const cols = 3
    const gutter = 18
    const top = 100
    const left = 120
    const totalWidth = width - left * 2
    const thumbW = (totalWidth - gutter * (cols - 1)) / cols
    const thumbH = thumbW * 0.7
    let x = left
    let y = top
    for (let i = 0; i < galleryImages.length; i++) {
      const rect = { index: i, x, y, w: thumbW, h: thumbH }
      ctx.save()
      ctx.beginPath()
      ctx.rect(rect.x, rect.y, rect.w, rect.h)
      ctx.clip()
      drawCoverImage(ctx, galleryThumbs[i], rect)
      ctx.restore()
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)'
      ctx.strokeRect(rect.x, rect.y, rect.w, rect.h)
      galleryThumbRects.push(rect)
      x += thumbW + gutter
      if ((i + 1) % cols === 0) {
        x = left
        y += thumbH + gutter
      }
    }

    ctx.fillStyle = 'rgba(255, 255, 255, 0.55)'
    ctx.font = '12px Helvetica, Arial, sans-serif'
    ctx.fillText('Tap a photo to view', 120, 600)
  }

  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'

  if (galleryPlaneTexture) galleryPlaneTexture.needsUpdate = true
}

function setGalleryImage(index) {
  if (!galleryPlane) return
  galleryIndex = (index + galleryImages.length) % galleryImages.length
  const url = galleryImages[galleryIndex]
  galleryPlaneImage = new Image()
  galleryPlaneImage.src = url
  galleryPlaneImage.onload = () => drawGalleryTexture()
  drawGalleryTexture()
}

function applyGalleryControls() {
  controls.enablePan = false
  controls.enableZoom = false
  controls.enableRotate = true
  controls.minAzimuthAngle = -0.4
  controls.maxAzimuthAngle = 0.4
  controls.minPolarAngle = Math.PI / 2 - 0.25
  controls.maxPolarAngle = Math.PI / 2 + 0.25
}

function applyVideosControls() {
  controls.enablePan = false
  controls.enableZoom = false
  controls.enableRotate = true
  controls.minAzimuthAngle = -0.5
  controls.maxAzimuthAngle = 0.5
  controls.minPolarAngle = Math.PI / 2 - 0.35
  controls.maxPolarAngle = Math.PI / 2 + 0.35
}

function openVideosScene() {
  if (videosState !== 'closed') return
  createPortfolioScene()
  captureControlsDefaults()
  videosState = 'opening'
  videosTransitionStart = clock.getElapsedTime()
  if (portfolioGroup) portfolioGroup.visible = true
  if (heroCube) heroCube.visible = false
  if (linkMeshes.length) {
    for (const mesh of linkMeshes) mesh.visible = false
  }
  if (nameMeshRef) nameMeshRef.visible = false
  if (subtitleMeshRef) subtitleMeshRef.visible = false
  applyVideosControls()
}

function closeVideosScene() {
  if (videosState === 'closed' || videosState === 'closing') return
  videosState = 'closing'
  videosTransitionStart = clock.getElapsedTime()
  videosCloseFromPosition.copy(camera.position)
  videosCloseFromTarget.copy(controls.target)
  if (videoDetailState !== 'closed') closeVideoDetail()
}

function openVideoDetail(project) {
  if (videoDetailState !== 'closed') return
  createVideoDetailPlane(project)
  videoDetailState = 'opening'
  videoDetailTransitionStart = clock.getElapsedTime()
  videoDetailPlane.visible = true
  videoDetailPlane.position.set(0, 0, 0.65)
}

function closeVideoDetail() {
  if (videoDetailState === 'closed' || videoDetailState === 'closing') return
  videoDetailState = 'closing'
  videoDetailTransitionStart = clock.getElapsedTime()
  videoDetailCloseFromPosition.copy(camera.position)
  videoDetailCloseFromTarget.copy(controls.target)
}
function openGallery() {
  if (galleryState !== 'closed') return
  createGalleryPlane()
  captureControlsDefaults()
  galleryState = 'opening'
  galleryTransitionStart = clock.getElapsedTime()
  bodyEl.classList.add('gallery-open')
  galleryPlane.visible = true
  galleryPlane.position.copy(galleryPlaneTarget)
  galleryPlane.position.z = 0.5
  galleryMode = 'grid'
  drawGalleryTexture()
  applyGalleryControls()
}

function closeGallery() {
  if (galleryState === 'closed' || galleryState === 'closing') return
  galleryState = 'closing'
  galleryTransitionStart = clock.getElapsedTime()
  galleryCloseFromPosition.copy(camera.position)
  galleryCloseFromTarget.copy(controls.target)
  bodyEl.classList.remove('gallery-open')
}

function registerStartupText(mesh) {
  if (!mesh) return
  mesh.userData.basePos = mesh.position.clone()
  mesh.userData.startupOffset = new THREE.Vector3(0, 0, 0)
  startupTextMeshes.push(mesh)
}

function runStartupSpin() {
  if (startupSpinPlayed) return
  if (!donuts.length) {
    startupSpinPending = true
    return
  }

  startupSpinPlayed = true
  startupSpinPending = false
  const now = clock.getElapsedTime()

  for (const donut of donuts) {
    const spin = donut.spin
    if (!spin) continue
    const delay = 0
    spin.duration = 1.05
    spin.startRotX = spin.baseRotX - Math.PI * 2
    spin.t0 = now + delay
    spin.active = true
    spin.isStartup = true
    donut.rotation.x = spin.startRotX
    donut.pos.copy(donut.basePos).multiplyScalar(0.2)
  }

  for (const mesh of startupTextMeshes) {
    const basePos = mesh.userData.basePos ?? mesh.position.clone()
    const spin = mesh.userData.startupSpin ?? {}
    const delay = 0
    spin.duration = 1.05
    spin.baseRotX = mesh.rotation.x
    spin.startRotX = spin.baseRotX - Math.PI * 2
    spin.t0 = now + delay
    spin.active = true
    spin.isStartup = true
    const isLinkText = Boolean(mesh.userData.label && linkTargets[mesh.userData.label])
    const pull = isLinkText ? -0.5 : -0.8
    spin.startOffset = mesh.userData.noStartupMove
      ? new THREE.Vector3(0, 0, 0)
      : basePos.clone().multiplyScalar(pull)
    mesh.userData.startupSpin = spin
    mesh.userData.startupOffset.copy(spin.startOffset)
    mesh.rotation.x = spin.startRotX
  }
}

function triggerStartupSpin() {
  if (mobileQuery.matches) {
    window.setTimeout(runStartupSpin, 220)
    return
  }
  runStartupSpin()
}

function startIntroMotion() {
  if (
    introActive ||
    aboutState !== 'closed' ||
    galleryState !== 'closed' ||
    reelState !== 'closed' ||
    videosState !== 'closed'
  ) {
    return
  }
  introActive = true
  introStart = clock.getElapsedTime()
  introFromTarget.copy(controls.target)
  const introDir = cameraBasePosition.clone().normalize()
  const introDistance = mobileQuery.matches ? 16 : 10
  const introLift = mobileQuery.matches ? 1.6 : 1.2
  introDuration = mobileQuery.matches ? 1.6 : INTRO_DURATION
  const introPos = introDir.multiplyScalar(introDistance).add(new THREE.Vector3(0, introLift, 0))
  introFromPosition.copy(introPos)
  camera.position.copy(introPos)
  controls.target.copy(anchor.name)
  controls.update()
}

loadingManager.onLoad = () => {
  texturesReady = true
  tryHideMobileLoader()
}
loadingManager.onError = () => {
  texturesReady = true
  tryHideMobileLoader()
}

/**
 * Camera
 */
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
const START_CAMERA_RADIUS = 2.25
const START_CAMERA_Y = 0.2
const START_CAMERA_AZIMUTH = THREE.MathUtils.degToRad(-10)
camera.position.set(
  Math.sin(START_CAMERA_AZIMUTH) * START_CAMERA_RADIUS,
  START_CAMERA_Y,
  Math.cos(START_CAMERA_AZIMUTH) * START_CAMERA_RADIUS
)
scene.add(camera)
const cameraBasePosition = camera.position.clone()

/**
 * Renderer sizing
 */
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(getPixelRatio())

window.addEventListener('resize', () => {
  sizes.width = window.innerWidth
  sizes.height = window.innerHeight

  camera.aspect = sizes.width / sizes.height
  camera.updateProjectionMatrix()

  renderer.setSize(sizes.width, sizes.height)
  renderer.setPixelRatio(getPixelRatio())

  updateLinkAnchors()
  fitTextToViewport()
  updateAboutLayout()
  if (heroCube && heroCube.userData.baseScale) {
    const scale = mobileQuery.matches ? 0.75 : 0.85
    heroCube.scale.setScalar(scale)
    heroCube.userData.baseScale.copy(heroCube.scale)
  }
  if (heroClosePlane) heroClosePlane.scale.setScalar(mobileQuery.matches ? 0.85 : 1)
  computeRatedSpawnTarget()
  if (ratedRoot && ratedReady && ratedState === 'idle') updateRatedSpawnPose()
})

/**
 * Interaction (hover + click)
 */
const raycaster = new THREE.Raycaster()
raycaster.layers.enable(1)
const pointerNDC = new THREE.Vector2()
const interactive = []
let hovered = null
let lastHoverTime = 0
const hoverGraceMs = 90
const hoverScaleTarget = new THREE.Vector3()
const donutTarget = new THREE.Vector3()
const imageTarget = new THREE.Vector3()
let lastCanvasClick = 0
const clickDownPos = new THREE.Vector2()
let clickDownActive = false
let clickDownInBottom = false
let pointerInsideCanvas = false
const squarespaceBaseUrl = 'https://alexanderedep.com'
const parentOrigin = 'https://alexanderedep.com'
const linkTargets = {
  videos: `${squarespaceBaseUrl}/videos`,
  // about: `${squarespaceBaseUrl}/about`,
  photos: `${squarespaceBaseUrl}/photos`
}

function setPointerFromEvent(e) {
  const rect = renderer.domElement.getBoundingClientRect()
  const x = ((e.clientX - rect.left) / rect.width) * 2 - 1
  const y = -(((e.clientY - rect.top) / rect.height) * 2 - 1)
  pointerNDC.set(x, y)
}

function getIntersect() {
  raycaster.setFromCamera(pointerNDC, camera)
  const hits = raycaster.intersectObjects(interactive, true)
  return hits.length ? hits[0] : null
}

function getRatedClickHit() {
  raycaster.setFromCamera(pointerNDC, camera)
  const hits = raycaster.intersectObjects(interactive, true)
  if (!hits.length) return null
  for (const hit of hits) {
    const ratedRoot = getRatedRoot(hit.object)
    if (ratedRoot) return ratedRoot
  }
  return null
}

function getHoverIntersect() {
  raycaster.setFromCamera(pointerNDC, camera)
  const hits = raycaster.intersectObjects(interactive, true)
  if (!hits.length) return null
  let donutHit = null
  let meshHit = null
  for (const hit of hits) {
    if (hit.object === donutMesh && typeof hit.instanceId === 'number') {
      if (!donutHit) donutHit = hit
    } else if (!meshHit) {
      meshHit = hit
    }
    if (donutHit && meshHit) break
  }
  if (donutHit) {
    if (!meshHit) return donutHit
    const blocker = meshHit.object.userData?.blocksDonutHover
    const isClickable = Boolean(meshHit.object.userData?.href || meshHit.object.userData?.clickable)
    if (blocker === false || !isClickable) return donutHit
  }
  return meshHit || donutHit
}
function getRatedRoot(obj) {
  let current = obj
  while (current) {
    if (current === ratedRoot) return ratedRoot
    current = current.parent
  }
  return null
}


if (hoverCapableQuery.matches && !mobileQuery.matches) {
  const updateHoverState = () => {
    if (heroDragActive) return
    if (aboutState !== 'closed' || galleryState !== 'closed' || reelState !== 'closed') return
    if (!pointerInsideCanvas) return
    const hit = getHoverIntersect()
    if (hit) lastHoverTime = performance.now()

    let nextHover = null
    if (nameMeshRef && nameHitPlane) {
      const nameHit = raycaster.intersectObject(nameHitPlane, true)
      if (nameHit.length) {
        nextHover = { type: 'mesh', mesh: nameMeshRef }
      }
    }
    if (!nextHover && hit) {
      const ratedRoot = getRatedRoot(hit.object)
      if (ratedRoot) {
        nextHover = { type: 'mesh', mesh: ratedRoot }
      } else if (ratedHitMesh && hit.object === ratedHitMesh) {
        nextHover = { type: 'mesh', mesh: ratedRoot }
      } else if (hit.object === donutMesh && typeof hit.instanceId === 'number') {
        nextHover = { type: 'donut', id: hit.instanceId }
      } else {
        nextHover = { type: 'mesh', mesh: hit.object }
      }
    } else if (
      !nextHover &&
      hovered &&
      hovered.type === 'mesh' &&
      hovered.mesh.userData.hoverSticky &&
      performance.now() - lastHoverTime < hoverGraceMs
    ) {
      nextHover = hovered
    }

    const hoverChanged =
      !hovered ||
      !nextHover ||
      hovered.type !== nextHover.type ||
      (hovered.type === 'mesh' && hovered.mesh !== nextHover.mesh) ||
      (hovered.type === 'donut' && hovered.id !== nextHover.id)

    if (hovered && hoverChanged) {
      if (hovered.type === 'mesh') {
        if (hovered.mesh.userData.baseScale) hovered.mesh.userData.targetScale = 1
        if (typeof hovered.mesh.userData.onHoverOut === 'function') hovered.mesh.userData.onHoverOut()
      } else if (hovered.type === 'donut') {
        resetSpread(donuts, (item) => item.spreadOffset)
      }
    }

    hovered = nextHover

    if (hovered) {
      if (hovered.type === 'mesh') {
        const interactionAllowed = hovered.mesh.userData.interactEnabled !== false
        const isClickable = interactionAllowed && Boolean(hovered.mesh.userData.href || hovered.mesh.userData.clickable)
        renderer.domElement.style.cursor = isClickable ? 'pointer' : 'default'
        if (interactionAllowed) {
          if (hovered.mesh.userData.baseScale) hovered.mesh.userData.targetScale = 1.12
          if (typeof hovered.mesh.userData.onHoverIn === 'function') hovered.mesh.userData.onHoverIn()
        }
      } else if (hovered.type === 'donut') {
        renderer.domElement.style.cursor = 'default'
        const donut = donuts[hovered.id]
        if (donut) {
          spreadFrom(donut.basePos, donuts, (item) => item.basePos, (item) => item.spreadOffset)
        }
      }
    } else {
      renderer.domElement.style.cursor = 'default'
    }
  }

  renderer.domElement.addEventListener('pointermove', (e) => {
    if (aboutState !== 'closed' || galleryState !== 'closed' || reelState !== 'closed') return
    setPointerFromEvent(e)
    updateHoverState()
  })
  renderer.domElement.addEventListener('pointerenter', () => {
    pointerInsideCanvas = true
  })
  renderer.domElement.addEventListener('pointerleave', () => {
    pointerInsideCanvas = false
    hovered = null
    renderer.domElement.style.cursor = 'default'
  })

  const hoverTick = () => {
    updateHoverState()
  }
  window.addEventListener('pointermove', hoverTick)
}

function handleCanvasClick(e) {
  if (performance.now() < nameClickLockUntil) return
  if (
    heroState !== 'idle' &&
    clickDownInBottom &&
    e.clientY > window.innerHeight * 0.7
  ) {
    closeHeroCube()
    return
  }
  if (ratedState === 'open') {
    setPointerFromEvent(e)
    const ratedClick = getRatedClickHit()
    if (ratedClick && typeof ratedClick.userData.onClick === 'function') {
      ratedClick.userData.onClick()
      triggerRatedSquish()
      return
    }
    if (e.clientY > window.innerHeight * 0.7) {
      closeRatedFocus()
      return
    }
  }
  if (heroState !== 'idle') {
    setPointerFromEvent(e)
    const hit = getIntersect()
    if (hit && (hit.object === heroCube || hit.object === heroHitMesh)) return
  }
  if (performance.now() - heroDragLastUp < 180 || heroDragMoved) return
  if (performance.now() - ratedDragLastUp < 180 || ratedDragMoved) return
  if (heroState !== 'idle' && !(heroState === 'closing' && !heroCube?.visible)) {
    setPointerFromEvent(e)
    if (heroClosePlane && heroClosePlane.visible) {
      raycaster.setFromCamera(pointerNDC, camera)
      const hits = raycaster.intersectObject(heroClosePlane, true)
      if (hits.length && typeof heroClosePlane.userData.onClick === 'function') {
        heroClosePlane.userData.onClick()
        return
      }
    }
    return
  }
  if (aboutState === 'closed' && nameMeshRef && nameHitPlane) {
    setPointerFromEvent(e)
    raycaster.setFromCamera(pointerNDC, camera)
    const hits = raycaster.intersectObject(nameHitPlane, true)
    if (hits.length) {
      if (typeof nameMeshRef.userData.onClick === 'function') nameMeshRef.userData.onClick()
      return
    }
  }
  if (
    aboutState === 'closed' &&
    hovered &&
    hovered.type === 'mesh' &&
    hovered.mesh === nameMeshRef
  ) {
    if (typeof nameMeshRef.userData.onClick === 'function') nameMeshRef.userData.onClick()
    return
  }
  if (aboutState !== 'closed') {
    setPointerFromEvent(e)
    raycaster.setFromCamera(pointerNDC, camera)
    if (aboutPanelPlane && aboutPanelPlane.visible) {
      const hits = raycaster.intersectObject(aboutPanelPlane, true)
      if (hits.length && hits[0].uv) {
        const uv = hits[0].uv
        const px = uv.x * ABOUT_CANVAS_SIZE.width
        const py = (1 - uv.y) * ABOUT_CANVAS_SIZE.height
        if (
          px >= ABOUT_CLOSE_BOUNDS.x &&
          px <= ABOUT_CLOSE_BOUNDS.x + ABOUT_CLOSE_BOUNDS.w &&
          py >= ABOUT_CLOSE_BOUNDS.y &&
          py <= ABOUT_CLOSE_BOUNDS.y + ABOUT_CLOSE_BOUNDS.h
        ) {
          closeAboutCard()
        }
      }
    }
    return
  }
  if (ratedRoot && !ratedReady) return
  if (videoDetailState !== 'closed') {
    setPointerFromEvent(e)
    raycaster.setFromCamera(pointerNDC, camera)
    if (videoDetailPlane && videoDetailPlane.visible) {
      const hits = raycaster.intersectObject(videoDetailPlane, true)
      if (hits.length && hits[0].uv) {
        const uv = hits[0].uv
        const px = uv.x * 1024
        const py = (1 - uv.y) * 640
        if (
          px >= VIDEO_DETAIL_CLOSE_BOUNDS.x &&
          px <= VIDEO_DETAIL_CLOSE_BOUNDS.x + VIDEO_DETAIL_CLOSE_BOUNDS.w &&
          py >= VIDEO_DETAIL_CLOSE_BOUNDS.y &&
          py <= VIDEO_DETAIL_CLOSE_BOUNDS.y + VIDEO_DETAIL_CLOSE_BOUNDS.h
        ) {
          closeVideoDetail()
        }
      }
    }
    return
  }
  if (videosState !== 'closed') {
    setPointerFromEvent(e)
    const hit = getIntersect()
    if (hit && typeof hit.object.userData.onClick === 'function') {
      hit.object.userData.onClick()
    }
    return
  }
  if (reelState !== 'closed') {
    setPointerFromEvent(e)
    raycaster.setFromCamera(pointerNDC, camera)
    if (reelPlane && reelPlane.visible) {
      const hits = raycaster.intersectObject(reelPlane, true)
      if (hits.length && hits[0].uv) {
        const uv = hits[0].uv
        const px = uv.x * REEL_CANVAS_SIZE.width
        const py = (1 - uv.y) * REEL_CANVAS_SIZE.height
        const bounds = getReelBounds()
        if (
          px >= bounds.close.x &&
          px <= bounds.close.x + bounds.close.w &&
          py >= bounds.close.y &&
          py <= bounds.close.y + bounds.close.h
        ) {
          closeReelCard()
        } else if (
          px >= bounds.image.x &&
          px <= bounds.image.x + bounds.image.w &&
          py >= bounds.image.y &&
          py <= bounds.image.y + bounds.image.h
        ) {
          window.open(REEL_URL, '_blank', 'noopener')
        }
      }
    }
    return
  }
  if (galleryState !== 'closed') {
    setPointerFromEvent(e)
    raycaster.setFromCamera(pointerNDC, camera)
    if (galleryPlane && galleryPlane.visible) {
      const hits = raycaster.intersectObject(galleryPlane, true)
      if (hits.length && hits[0].uv) {
        const uv = hits[0].uv
        const px = uv.x * GALLERY_CANVAS_SIZE.width
        const py = (1 - uv.y) * GALLERY_CANVAS_SIZE.height
        if (galleryMode === 'grid') {
          for (const rect of galleryThumbRects) {
            if (
              px >= rect.x &&
              px <= rect.x + rect.w &&
              py >= rect.y &&
              py <= rect.y + rect.h
            ) {
              galleryMode = 'single'
              setGalleryImage(rect.index)
              break
            }
          }
          return
        }
        if (
          px >= GALLERY_CLOSE_BOUNDS.x &&
          px <= GALLERY_CLOSE_BOUNDS.x + GALLERY_CLOSE_BOUNDS.w &&
          py >= GALLERY_CLOSE_BOUNDS.y &&
          py <= GALLERY_CLOSE_BOUNDS.y + GALLERY_CLOSE_BOUNDS.h
        ) {
          closeGallery()
        } else if (
          px >= GALLERY_BACK_BOUNDS.x &&
          px <= GALLERY_BACK_BOUNDS.x + GALLERY_BACK_BOUNDS.w &&
          py >= GALLERY_BACK_BOUNDS.y &&
          py <= GALLERY_BACK_BOUNDS.y + GALLERY_BACK_BOUNDS.h
        ) {
          galleryMode = 'grid'
          drawGalleryTexture()
        } else if (
          px >= GALLERY_PREV_BOUNDS.x &&
          px <= GALLERY_PREV_BOUNDS.x + GALLERY_PREV_BOUNDS.w &&
          py >= GALLERY_PREV_BOUNDS.y &&
          py <= GALLERY_PREV_BOUNDS.y + GALLERY_PREV_BOUNDS.h
        ) {
          setGalleryImage(galleryIndex - 1)
        } else if (
          px >= GALLERY_NEXT_BOUNDS.x &&
          px <= GALLERY_NEXT_BOUNDS.x + GALLERY_NEXT_BOUNDS.w &&
          py >= GALLERY_NEXT_BOUNDS.y &&
          py <= GALLERY_NEXT_BOUNDS.y + GALLERY_NEXT_BOUNDS.h
        ) {
          setGalleryImage(galleryIndex + 1)
        }
      }
    }
    return
  }
  setPointerFromEvent(e)
  let nameHit = null
  if (nameMeshRef && nameHitPlane) {
    const hits = raycaster.intersectObject(nameHitPlane, true)
    if (hits.length) nameHit = hits[0]
  }
  const hit = nameHit ?? getIntersect()
  if (!hit) return
  if (nameHit) {
    if (typeof nameMeshRef.userData.onClick === 'function') nameMeshRef.userData.onClick()
    return
  }
  if (ratedRoot && ratedHitMesh && hit.object === ratedHitMesh) {
    if (typeof ratedRoot.userData.onClick === 'function') ratedRoot.userData.onClick()
    triggerRatedSquish()
    return
  }
  const ratedHitRoot = getRatedRoot(hit.object)
  if (ratedHitRoot) {
    if (typeof ratedHitRoot.userData.onClick === 'function') ratedHitRoot.userData.onClick()
    triggerRatedSquish()
    return
  }
  if (heroCube && hit.object === heroCube) return
  if (hit.object === donutMesh && typeof hit.instanceId === 'number') {
    const donut = donuts[hit.instanceId]
    if (!donut) return
    const spin = donut.spin
    const delta = donut.rotation.x - spin.baseRotX
    spin.startRotX = spin.baseRotX + THREE.MathUtils.euclideanModulo(delta, Math.PI * 2)
    spin.t0 = clock.getElapsedTime()
    spin.active = true
    return
  }
  if (typeof hit.object.userData.onClick === 'function') hit.object.userData.onClick()
}

function enableNameInteraction() {
  nameInteractEnabled = true
  if (nameMeshRef) {
    nameMeshRef.userData.interactEnabled = true
    nameMeshRef.userData.clickable = true
  }
}

renderer.domElement.addEventListener('pointerdown', (e) => {
  clickDownActive = true
  clickDownPos.set(e.clientX, e.clientY)
  clickDownInBottom = e.clientY > window.innerHeight * 0.7
  if (heroState === 'closing') {
    heroState = 'idle'
    if (heroCube) heroCube.visible = false
    if (heroDonutMesh) heroDonutMesh.visible = false
    if (heroClosePlane) heroClosePlane.visible = false
    if (nameHitPlane) nameHitPlane.visible = true
    if (nameMeshRef) {
      nameMeshRef.userData.interactEnabled = true
      nameMeshRef.userData.clickable = true
    }
  }
})

window.addEventListener('pointerup', (e) => {
  if (ratedDragActive) return
  if (heroDragActive && heroDragMoved) return
  const dx = e.clientX - clickDownPos.x
  const dy = e.clientY - clickDownPos.y
  if (clickDownActive && dx * dx + dy * dy > 36) {
    clickDownActive = false
    clickDownInBottom = false
    return
  }
  clickDownActive = false
  const rect = renderer.domElement.getBoundingClientRect()
  if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) return
  lastCanvasClick = performance.now()
  handleCanvasClick(e)
  clickDownInBottom = false
})

renderer.domElement.addEventListener('click', (e) => {
  if (performance.now() - lastCanvasClick < 200) return
  handleCanvasClick(e)
})

/**
 * Helpers
 */
function rand(min, max) {
  return min + Math.random() * (max - min)
}
function dist(a, b) {
  return a.distanceTo(b)
}
function easeInOutCubic(x) {
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2
}
function easeOutCubic(x) {
  return 1 - Math.pow(1 - x, 3)
}

/**
 * Text layout anchors
 */
const anchor = {
  name: new THREE.Vector3(0, 0, 0),
  photos: new THREE.Vector3(-1.25, 0.95, 0),
  videos: new THREE.Vector3(1.25, 0.95, 0),
  about: new THREE.Vector3(-1.25, -1.5, 0),
  contact: new THREE.Vector3(1.25, -1.5, 0)
}

/**
 * Controls
 */
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.enablePan = false
controls.enableZoom = true
controls.enableRotate = true
controls.screenSpacePanning = false
controls.target.copy(anchor.name)
controls.update()

const aboutTarget = new THREE.Vector3(0, 0, 0)
const aboutCameraPosition = new THREE.Vector3(0, 0, 2.5)
const controlsDefaults = {
  enableRotate: controls.enableRotate,
  enableZoom: controls.enableZoom,
  enablePan: controls.enablePan,
  minDistance: controls.minDistance,
  maxDistance: controls.maxDistance,
  minAzimuthAngle: controls.minAzimuthAngle,
  maxAzimuthAngle: controls.maxAzimuthAngle,
  minPolarAngle: controls.minPolarAngle,
  maxPolarAngle: controls.maxPolarAngle,
  target: controls.target.clone(),
  position: camera.position.clone()
}

/**
 * Floating link texts
 */
const fontLoader = new FontLoader(loadingManager)
let uiFont = null
const floatingTexts = []
const linkTilt = new THREE.Euler(0, 0, 0)
let nameMeshRef = null
let nameHitPlane = null
let subtitleMeshRef = null
const nameText = 'alex edep'
const textMeshes = []
const linkMeshes = []
let nameInteractEnabled = false
let nameClickLockUntil = 0
const textBlockers = []
const INTRO_TEXT_BLOCKER_PADDING = 0.28
const subtitleSpin = {
  active: false,
  baseRotX: 0,
  startRotX: 0,
  duration: 0.65,
  t0: 0
}
function getTextQuality(isMobile) {
  if (!isMobile) {
    return {
      curveSegments: 12,
      bevelSegments: 5,
      bevelThickness: 0.03,
      bevelSize: 0.02
    }
  }
  return {
    curveSegments: 6,
    bevelSegments: 2,
    bevelThickness: 0.02,
    bevelSize: 0.012
  }
}

function getSubtitleQuality(isMobile) {
  if (!isMobile) {
    return {
      curveSegments: 12,
      bevelSegments: 4,
      bevelThickness: 0.02,
      bevelSize: 0.012
    }
  }
  return {
    curveSegments: 6,
    bevelSegments: 2,
    bevelThickness: 0.016,
    bevelSize: 0.01
  }
}

function fitTextToViewport() {
  if (!textMeshes.length) return
  if (
    aboutState !== 'closed' ||
    galleryState !== 'closed' ||
    reelState !== 'closed' ||
    videosState !== 'closed'
  ) {
    pendingFitText = true
    return
  }

  const isMobile = window.innerWidth <= 768
  if (!isMobile) {
    const bounds = new THREE.Box3()
    const temp = new THREE.Box3()
    for (const mesh of textMeshes) {
      temp.setFromObject(mesh)
      bounds.union(temp)
    }
    const sphere = new THREE.Sphere()
    bounds.getBoundingSphere(sphere)
    const radius = sphere.radius || 0.01
    const fovRad = THREE.MathUtils.degToRad(camera.fov)
    const fovH = 2 * Math.atan(Math.tan(fovRad / 2) * camera.aspect)
    const distanceV = radius / Math.sin(fovRad / 2)
    const distanceH = radius / Math.sin(fovH / 2)
    const requiredDistance = Math.max(distanceV, distanceH) * 1.15
    const dir = cameraBasePosition.clone().normalize()
    const currentDistance = cameraBasePosition.length()
    const nextDistance = Math.max(currentDistance, requiredDistance)
    camera.position.copy(dir.multiplyScalar(nextDistance))
    controls.target.copy(anchor.name)
    controls.update()
    cameraBasePosition.copy(camera.position)
    controlsDefaults.position.copy(camera.position)
    controlsDefaults.target.copy(anchor.name)
    return
  }

  const bounds = new THREE.Box3()
  const temp = new THREE.Box3()
  for (const mesh of textMeshes) {
    temp.setFromObject(mesh)
    bounds.union(temp)
  }
  const textWidth = bounds.max.x - bounds.min.x
  const padding = mobileQuery.matches ? 2.55 : 1.15
  const fovRad = THREE.MathUtils.degToRad(camera.fov)
  const distance = (textWidth * padding) / (2 * Math.tan(fovRad / 2) * camera.aspect)
  const landingDistance = mobileQuery.matches ? distance * 0.5 : distance

  const landingY = mobileQuery.matches ? -0.15 : 0
  if (mobileQuery.matches) {
    const landingAzimuth = THREE.MathUtils.degToRad(-15)
    camera.position.set(
      Math.sin(landingAzimuth) * landingDistance,
      landingY,
      Math.cos(landingAzimuth) * landingDistance
    )
  } else {
    camera.position.set(0, landingY, landingDistance)
  }
  controls.target.copy(anchor.name)
  controls.update()
  cameraBasePosition.copy(camera.position)
  controlsDefaults.position.copy(camera.position)
  controlsDefaults.target.copy(anchor.name)
}

function updateLinkAnchors() {
  const scale = mobileQuery.matches ? 0.85 : 1
  const linkX = 1.25 * scale
  const linkY = 0.95 * scale
  const subtitleY = -0.55 * scale
  const bottomLinkY = subtitleY - linkY
  anchor.photos.set(-linkX, linkY, 0)
  anchor.videos.set(linkX, linkY, 0)
  anchor.about.set(-linkX, bottomLinkY, 0)
  anchor.contact.set(linkX, bottomLinkY, 0)

  if (!linkMeshes.length) return
  for (const item of floatingTexts) {
    const mesh = item.mesh
    if (!linkMeshes.includes(mesh)) continue
    if (mesh.userData.label === 'photos') item.basePos.copy(anchor.photos)
    if (mesh.userData.label === 'videos') item.basePos.copy(anchor.videos)
    if (mesh.userData.label === 'about') item.basePos.copy(anchor.about)
    if (mesh.userData.label === 'contact') item.basePos.copy(anchor.contact)
    mesh.position.copy(item.basePos)
    mesh.userData.basePos.copy(item.basePos)
  }

  if (ratedRoot && ratedReady && ratedState === 'idle') {
    updateRatedSpawnPose()
  }
}

function updateTextBlockers() {
  textBlockers.length = 0
  const bounds = new THREE.Box3()
  const center = new THREE.Vector3()
  for (const mesh of textMeshes) {
    bounds.setFromObject(mesh)
    bounds.getCenter(center)
    const radius = Math.max(0.01, bounds.getSize(new THREE.Vector3()).length() * 0.5)
    textBlockers.push({ center: center.clone(), radius })
  }
}

function applyRatedPosition(target) {
  if (!ratedRoot) return
  ratedRoot.position.copy(target)
}

function computeRatedSpawnTargetFrom(basePosition, target) {
  ratedSpawnCameraPos.copy(basePosition)
  ratedSpawnForward.copy(target).sub(basePosition).normalize()
  ratedSpawnRight.copy(ratedSpawnForward).cross(new THREE.Vector3(0, 1, 0)).normalize()
  ratedSpawnUp.copy(ratedSpawnRight).cross(ratedSpawnForward).normalize()

  const fovRad = THREE.MathUtils.degToRad(camera.fov)
  const height = 2 * Math.tan(fovRad / 2) * RATED_SPAWN_DISTANCE
  const width = height * camera.aspect
  const offset = mobileQuery.matches ? RATED_SPAWN_OFFSET_MOBILE : RATED_SPAWN_OFFSET

  ratedSpawnTarget.copy(basePosition)
  ratedSpawnTarget.add(ratedSpawnForward.clone().multiplyScalar(RATED_SPAWN_DISTANCE))
  ratedSpawnTarget.add(ratedSpawnRight.multiplyScalar(width * offset.x))
  ratedSpawnTarget.add(ratedSpawnUp.multiplyScalar(height * offset.y))
  ratedSpawnReady = true
}

function computeRatedSpawnTarget() {
  computeRatedSpawnTargetFrom(cameraBasePosition, anchor.name)
}

function updateRatedSpawnPose() {
  if (!ratedSpawnReady) computeRatedSpawnTarget()
  if (!ratedRoot || !ratedReady) return
  ratedSpawnPos.copy(ratedSpawnTarget)
  applyRatedPosition(ratedSpawnPos)

  const lookTarget = ratedSpawnCameraPos.clone()
  ratedRoot.lookAt(lookTarget)
  ratedRoot.rotateX(ratedSpawnTilt.x)
  ratedRoot.rotateY(ratedSpawnTilt.y)
  ratedRoot.rotateZ(ratedSpawnTilt.z)
  ratedSpawnRot.copy(ratedRoot.rotation)
  ratedRoot.userData.baseRotation = ratedRoot.rotation.clone()
}

function ensureRatedLights() {
  if (ratedLightsReady) return
  ratedAmbientLight = new THREE.AmbientLight(0xffffff, 0.9)
  ratedKeyLight = new THREE.DirectionalLight(0xffffff, 1.2)
  ratedKeyLight.position.set(1.2, 1.0, 2.2)
  ratedFillLight = new THREE.DirectionalLight(0xffffff, 1.1)
  ratedFillLight.position.set(-1.8, 0.5, 1.9)
  ratedAmbientLight.layers.set(1)
  ratedKeyLight.layers.set(1)
  ratedFillLight.layers.set(1)
  camera.layers.enable(1)
  camera.add(ratedAmbientLight)
  camera.add(ratedKeyLight)
  camera.add(ratedFillLight)
  ratedLightsReady = true
}

function focusRatedModel() {
  if (!ratedRoot || !ratedReady || ratedState !== 'idle') return
  if (!ratedSpawnReady) computeRatedSpawnTarget()
  ratedState = 'focusing'
  ratedFocusStart = clock.getElapsedTime()
  ratedFocusFromPosition.copy(camera.position)
  ratedFocusFromTarget.copy(controls.target)
  ratedFocusFromModelPos.copy(ratedRoot.position)
  ratedFocusFromScale = ratedRoot.scale.x
  ratedFocusTargetScale = ratedFocusFromScale * 1.4
  ratedFocusTargetPos.copy(ratedRoot.position)
  ratedFocusCameraOffset.copy(camera.position).sub(controls.target)
  ratedFocusCameraPos.copy(ratedFocusTargetPos).add(ratedFocusCameraOffset)
  applyHeroControls()
  if (donutMesh) donutMesh.visible = false
  if (linkMeshes.length) {
    for (const mesh of linkMeshes) mesh.visible = false
  }
  if (nameMeshRef) nameMeshRef.visible = false
  if (subtitleMeshRef) subtitleMeshRef.visible = false
}

function closeRatedFocus() {
  if (!ratedRoot || ratedState === 'idle' || ratedState === 'closing') return
  ratedState = 'closing'
  ratedFocusStart = clock.getElapsedTime()
  ratedFocusFromPosition.copy(camera.position)
  ratedFocusFromTarget.copy(controls.target)
  ratedFocusFromModelPos.copy(ratedRoot.position)
  ratedFocusFromScale = ratedRoot.scale.x
}

function updateAboutLayout() {
  const isMobile = window.innerWidth <= 768
  const heroScale = isMobile ? 0.9 : 1
  const panelScale = isMobile ? 0.85 : 1
  const gutter = isMobile ? 0.08 : 0.06
  let heroHalfWidth = 0.45 * heroScale
  if (heroCube?.geometry) {
    if (!heroCube.geometry.boundingBox) heroCube.geometry.computeBoundingBox()
    const bounds = heroCube.geometry.boundingBox
    if (bounds) heroHalfWidth = ((bounds.max.x - bounds.min.x) * heroScale) * 0.5
  }
  let panelHalfWidth = 0.84 * panelScale
  if (aboutPanelPlane?.userData?.panelSize?.panelW) {
    panelHalfWidth = (aboutPanelPlane.userData.panelSize.panelW * panelScale) * 0.5
  }
  const cubeY = isMobile ? -0.05 : 0
  const panelY = 0
  aboutHeroPos.set(-(gutter * 0.5 + heroHalfWidth), cubeY, 0.1)
  aboutPanelPos.set(gutter * 0.5 + panelHalfWidth, panelY, 0.1)
  aboutCameraTarget.set(0, 0, 0.1)
  aboutCameraPos.copy(aboutCameraTarget).add(new THREE.Vector3(0, 0, isMobile ? 3.2 : 3.6))
  if (heroCube && heroMode === 'about') {
    heroCube.position.copy(aboutHeroPos)
    heroCube.scale.setScalar(heroScale)
    heroCube.userData.baseScale = heroCube.scale.clone()
  }
  if (aboutPanelPlane) {
    aboutPanelPlane.position.copy(aboutPanelPos)
    aboutPanelPlane.scale.setScalar(panelScale)
  }
  if (aboutDonutMesh) aboutDonutMesh.position.copy(aboutCameraTarget)
}

function loadRatedModel() {
  if (ratedReady) return
  gltfLoader.load('/models/rated_d.glb', (gltf) => {
    ratedModel = gltf.scene
    ratedRoot = new THREE.Group()
    ratedRoot.layers.set(1)
    ratedRoot.add(ratedModel)
    ratedModel.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = false
        child.receiveShadow = false
        child.layers.set(1)
      }
    })
    const box = new THREE.Box3().setFromObject(ratedModel)
    const size = new THREE.Vector3()
    box.getSize(size)
    const maxDim = Math.max(size.x, size.y, size.z) || 1
    const scale = 0.32 / maxDim
    ratedRoot.scale.setScalar(scale)
    const centeredBox = new THREE.Box3().setFromObject(ratedModel)
    const center = new THREE.Vector3()
    centeredBox.getCenter(center)
    ratedModel.position.sub(center)
    const ratedSphere = new THREE.Sphere()
    centeredBox.getBoundingSphere(ratedSphere)
    ratedBlockerRadius = Math.max(0.18, ratedSphere.radius * scale * 1.1)
    ratedRoot.userData.baseScale = ratedRoot.scale.clone()
    ratedRoot.userData.targetScale = 1
    ratedRoot.userData.clickable = true
    ratedRoot.userData.onHoverIn = () => {
      ratedRoot.userData.targetScale = 1.12
    }
    ratedRoot.userData.onHoverOut = () => {
      ratedRoot.userData.targetScale = 1
    }
    ratedRoot.userData.onClick = () => {
      triggerRatedSquish()
      if (ratedState === 'idle') focusRatedModel()
    }
    ratedRoot.userData.baseRotation = ratedRoot.rotation.clone()
    scene.add(ratedRoot)
    interactive.push(ratedRoot)
    const hitGeo = new THREE.BoxGeometry(size.x * 1.6, size.y * 1.6, size.z * 1.6)
    const hitMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthTest: false, depthWrite: false })
    ratedHitMesh = new THREE.Mesh(hitGeo, hitMat)
    ratedHitMesh.userData.isRatedHit = true
    ratedRoot.add(ratedHitMesh)
    interactive.push(ratedHitMesh)
    ratedReady = true
    ensureRatedLights()
    updateLinkAnchors()
    updateRatedSpawnPose()
  })
}

function triggerRatedSquish() {
  if (!ratedRoot) return
  const now = clock.getElapsedTime()
  const extra = THREE.MathUtils.randFloat(0.08, 0.22)
  ratedSquishAmount = ratedSquishActive
    ? Math.min(0.5, ratedSquishAmount + extra)
    : THREE.MathUtils.randFloat(0.16, 0.35)
  ratedSquishWobble = THREE.MathUtils.randFloat(3.0, 6.0)
  ratedSquishDamp = THREE.MathUtils.randFloat(1.6, 3.0)
  ratedSquishActive = true
  ratedSquishStart = now
}

setupHeroCubeControls()
setupRatedControls()

function createHeroCube() {
  if (heroCube) return
  createHeroSparks()
  createHeroDebris()
  const geometry = new THREE.BoxGeometry(0.9, 0.9, 0.9, HERO_CHIP_SEGMENTS, HERO_CHIP_SEGMENTS, HERO_CHIP_SEGMENTS).toNonIndexed()
  const materials = Array.from({ length: 6 }, (_, i) => {
    const tex = heroTexture.clone()
    tex.wrapS = THREE.ClampToEdgeWrapping
    tex.wrapT = THREE.ClampToEdgeWrapping
    tex.colorSpace = THREE.SRGBColorSpace
    tex.magFilter = THREE.NearestFilter
    tex.minFilter = THREE.NearestFilter
    tex.generateMipmaps = false
    tex.repeat.set(1, 1)
    tex.offset.set(0, 0)
    tex.center.set(0.5, 0.5)
    const isSideFace = i === 0 || i === 1 || i === 4 || i === 5
    tex.rotation = isSideFace ? Math.PI : 0
    tex.needsUpdate = true
    heroFaceDrift.push({
      speedX: 0,
      speedY: 0
    })
    return new THREE.MeshBasicMaterial({ map: tex })
  })
  heroCube = new THREE.Mesh(geometry, materials)
  heroCube.userData.faceMaterials = materials
  heroCube.userData.basePositions = geometry.attributes.position.array.slice()
  heroCube.userData.baseNormals = geometry.attributes.normal.array.slice()
  heroCube.userData.vertexFaceIndex = new Int8Array(geometry.attributes.position.count).fill(-1)
  heroCube.userData.damage = new Float32Array(geometry.attributes.position.count)
  heroCube.userData.damageNoise = new Float32Array(geometry.attributes.position.count)
  heroCube.userData.triRemoved = new Uint8Array(Math.floor(geometry.attributes.position.count / 3))
  heroCube.userData.faceCellTris = Array.from({ length: 6 }, () => new Map())
  heroCube.userData.removedCells = Array.from({ length: 6 }, () => new Set())
  for (let i = 0; i < heroCube.userData.damageNoise.length; i++) {
    heroCube.userData.damageNoise[i] = THREE.MathUtils.randFloat(0.6, 1.4)
  }
  if (geometry.groups?.length) {
    for (const group of geometry.groups) {
      const start = group.start
      const end = group.start + group.count
      for (let i = start; i < end; i++) {
        heroCube.userData.vertexFaceIndex[i] = group.materialIndex
      }
    }
  }
  const half = 0.45
  const size = 0.9
  const pos = geometry.attributes.position.array
  const triCount = Math.floor(geometry.attributes.position.count / 3)
  const getAxes = (faceId) => {
    switch (faceId) {
      case 0:
      case 1:
        return ['z', 'y']
      case 2:
      case 3:
        return ['x', 'z']
      case 4:
      case 5:
      default:
        return ['x', 'y']
    }
  }
  const coordAt = (axis, ix) => {
    if (axis === 'x') return pos[ix * 3]
    if (axis === 'y') return pos[ix * 3 + 1]
    return pos[ix * 3 + 2]
  }
  for (let tIndex = 0; tIndex < triCount; tIndex++) {
    const i0 = tIndex * 3
    const faceId = heroCube.userData.vertexFaceIndex[i0]
    if (faceId < 0) continue
    const [uAxis, vAxis] = getAxes(faceId)
    const cu =
      (coordAt(uAxis, i0) + coordAt(uAxis, i0 + 1) + coordAt(uAxis, i0 + 2)) / 3
    const cv =
      (coordAt(vAxis, i0) + coordAt(vAxis, i0 + 1) + coordAt(vAxis, i0 + 2)) / 3
    const u = Math.max(0, Math.min(HERO_CHIP_SEGMENTS - 1, Math.floor(((cu + half) / size) * HERO_CHIP_SEGMENTS)))
    const v = Math.max(0, Math.min(HERO_CHIP_SEGMENTS - 1, Math.floor(((cv + half) / size) * HERO_CHIP_SEGMENTS)))
    const key = `${u}_${v}`
    const map = heroCube.userData.faceCellTris[faceId]
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(tIndex)
  }
  heroCube.position.copy(anchor.name)
  heroCube.userData.baseScale = heroCube.scale.clone()
  heroCube.userData.targetScale = 1
  heroCube.scale.setScalar(mobileQuery.matches ? 0.75 : 0.85)
  heroCube.userData.baseScale.copy(heroCube.scale)
  heroCube.userData.clickable = true
  heroCube.userData.onHoverIn = () => {
    heroCube.userData.targetScale = 1.12
  }
  heroCube.userData.onHoverOut = () => {
    heroCube.userData.targetScale = 1
  }
  heroCube.userData.baseRotation = heroCube.rotation.clone()
  heroCube.rotation.set(Math.PI / 12, Math.PI / 4, 0)
  heroCube.userData.baseRotation.copy(heroCube.rotation)
  heroSpawnBaseRotation.copy(heroCube.rotation)
  heroCube.visible = false
  interactive.push(heroCube)
  scene.add(heroCube)

  if (!heroCoreMesh) {
    const coreGeom = new THREE.BoxGeometry(0.86, 0.86, 0.86)
    const coreMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: false,
      depthWrite: false
    })
    heroCoreMesh = new THREE.Mesh(coreGeom, coreMat)
    heroCoreMesh.renderOrder = 4
    heroCube.add(heroCoreMesh)
  }

  const edgeGeo = new THREE.EdgesGeometry(geometry, 20)
  const edgeMat = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.75 })
  const edgeLines = new THREE.LineSegments(edgeGeo, edgeMat)
  edgeLines.scale.setScalar(1.01)
  const edgeMat2 = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.45 })
  const edgeLines2 = new THREE.LineSegments(edgeGeo, edgeMat2)
  edgeLines2.scale.setScalar(1.03)
  heroCube.add(edgeLines)
  heroCube.add(edgeLines2)

  const hitGeo = new THREE.BoxGeometry(1.25, 1.25, 1.25)
  const hitMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthTest: false, depthWrite: false })
  heroHitMesh = new THREE.Mesh(hitGeo, hitMat)
  heroHitMesh.userData.isHeroHit = true
  heroCube.add(heroHitMesh)
  interactive.push(heroHitMesh)
}

function createHeroDonuts() {
  if (heroDonutMesh) return
  const donutGeometry = new THREE.BoxGeometry(0.35, 0.35, 0.35)
  heroDonutMesh = new THREE.InstancedMesh(donutGeometry, donutBaseMaterial.clone(), HERO_DONUT_COUNT)
  heroDonutMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)

  let tries = 0
  const maxTries = HERO_DONUT_COUNT * 200
  while (heroDonuts.length < HERO_DONUT_COUNT && tries < maxTries) {
    tries++
    const pos = new THREE.Vector3(
      (Math.random() - 0.5) * HERO_DONUT_RANGE,
      (Math.random() - 0.5) * HERO_DONUT_RANGE,
      (Math.random() - 0.5) * HERO_DONUT_RANGE - 0.6
    )
    if (pos.length() < HERO_DONUT_CLEAR) continue
    // Keep them behind the hero cube (camera is +z)
    if (pos.z > -0.3) continue

    const rotation = new THREE.Euler(Math.random() * Math.PI, Math.random() * Math.PI, 0)
    const s = THREE.MathUtils.randFloat(0.5, 1.1)
    const scale = new THREE.Vector3(s, s, s)

    heroDonuts.push({
      basePos: pos.clone(),
      pos: pos.clone(),
      rotation,
      rotSpeed: THREE.MathUtils.randFloat(0.2, 0.6),
      scale: scale.clone()
    })
  }

  for (let i = 0; i < heroDonuts.length; i++) {
    const donut = heroDonuts[i]
    heroDonutDummy.position.copy(donut.basePos)
    heroDonutDummy.rotation.copy(donut.rotation)
    heroDonutDummy.scale.copy(donut.scale)
    heroDonutDummy.updateMatrix()
    heroDonutMesh.setMatrixAt(i, heroDonutDummy.matrix)
  }
  heroDonutMesh.count = heroDonuts.length
  heroDonutMesh.instanceMatrix.needsUpdate = true
  heroDonutMesh.visible = false
  scene.add(heroDonutMesh)
}

function applyHeroControls() {
  controls.enablePan = false
  controls.enableZoom = true
  controls.enableRotate = true
  controls.minDistance = mobileQuery.matches ? HERO_ZOOM_MIN_MOBILE : HERO_ZOOM_MIN
  controls.maxDistance = mobileQuery.matches ? HERO_ZOOM_MAX_MOBILE : HERO_ZOOM_MAX
  controls.minAzimuthAngle = -Math.PI / 2
  controls.maxAzimuthAngle = Math.PI / 2
  controls.minPolarAngle = Math.PI / 2 - 0.5
  controls.maxPolarAngle = Math.PI / 2 + 0.5
  controls.target.copy(heroTargetPos)
}

function triggerHeroSquish(force = false) {
  if (!heroCube) return
  if (!force && heroDragActive && heroSquishUsedThisDrag) return
  const now = clock.getElapsedTime()
  if (!force && !heroDragActive && heroDragDistance <= 8) return
  if (now < heroSquishCooldownUntil) return
  heroSquishCooldownUntil = now + HERO_SQUISH_COOLDOWN
  heroSquishBaseScale.copy(heroCube.scale)
  const extra = force
    ? THREE.MathUtils.randFloat(0.02, 0.06)
    : THREE.MathUtils.randFloat(0.05, 0.14)
  const maxSquish = force ? 0.16 : 0.34
  heroSquishAmount = heroSquishActive
    ? Math.min(maxSquish, heroSquishAmount + extra)
    : (
      force
        ? THREE.MathUtils.randFloat(0.06, 0.12)
        : THREE.MathUtils.randFloat(0.12, 0.24)
    )
  heroSquishWobble = THREE.MathUtils.randFloat(2.2, 4.6)
  heroSquishDamp = THREE.MathUtils.randFloat(1.0, 2.0)
  heroSquishActive = true
  heroSquishStart = now
  if (heroDragActive) heroSquishUsedThisDrag = true
}

function triggerHeroShake(strength = 0.06) {
  if (heroCube && !heroShakeActive) {
    heroShakeBasePos.copy(heroCube.position)
    heroShakeBaseRot.set(heroCube.rotation.x, heroCube.rotation.y, heroCube.rotation.z)
  }
  heroShakeActive = true
  heroShakeTime = HERO_SHAKE_DURATION
  heroShakeStrength = Math.max(heroShakeStrength, strength)
}

function resolveHeroCubeHit(hit) {
  if (hit.object === heroHitMesh && heroCube) {
    const hits = raycaster.intersectObject(heroCube, false)
    if (hits.length) return hits[0]
  }
  return hit
}

function applyHeroChipFromHit(hit) {
  // Mining/destroy interactivity disabled; squish-only mode.
  return
  if (!heroCube?.geometry?.attributes?.position) return
  hit = resolveHeroCubeHit(hit)
  if (heroCube) heroCube.worldToLocal(heroChipPointLocal.copy(hit.point))
  if (hit.face?.normal) {
    heroChipNormalLocal.copy(hit.face.normal).normalize()
  } else {
    heroChipNormalLocal.set(0, 0, 1)
  }
  heroChipMaterialIndex = typeof hit.face?.materialIndex === 'number' ? hit.face.materialIndex : -1
  if (heroChipMaterialIndex < 0 && heroCube?.geometry && typeof hit.faceIndex === 'number') {
    const triStart = hit.faceIndex * 3
    const groups = heroCube.geometry.groups ?? []
    for (const group of groups) {
      if (triStart >= group.start && triStart < group.start + group.count) {
        heroChipMaterialIndex = group.materialIndex
        break
      }
    }
  }
  if (heroChipMaterialIndex >= 0 && heroFaceDrift[heroChipMaterialIndex]) {
    const drift = heroFaceDrift[heroChipMaterialIndex]
    drift.speedX *= 0.2
    drift.speedY *= 0.2
  }

  registerHeroChip()

  const geom = heroCube.geometry
  const posAttr = geom.attributes.position
  const basePositions = heroCube.userData.basePositions
  const damage = heroCube.userData.damage
  const faceCellTris = heroCube.userData.faceCellTris
  const removedCells = heroCube.userData.removedCells
  const segments = HERO_CHIP_SEGMENTS
  const half = 0.45
  const size = 0.9
  const getAxes = (faceId) => {
    switch (faceId) {
      case 0:
      case 1:
        return ['z', 'y']
      case 2:
      case 3:
        return ['x', 'z']
      case 4:
      case 5:
      default:
        return ['x', 'y']
    }
  }
  const [uAxis, vAxis] = getAxes(heroChipMaterialIndex)
  const coord = (axis) => {
    if (axis === 'x') return heroChipPointLocal.x
    if (axis === 'y') return heroChipPointLocal.y
    return heroChipPointLocal.z
  }
  const u = Math.max(0, Math.min(segments - 1, Math.floor(((coord(uAxis) + half) / size) * segments)))
  const v = Math.max(0, Math.min(segments - 1, Math.floor(((coord(vAxis) + half) / size) * segments)))
  const key = `${u}_${v}`
  const map = faceCellTris?.[heroChipMaterialIndex]
  const tris = map?.get(key) ?? []
  const cellSet = removedCells?.[heroChipMaterialIndex]
  const cellsToRemove = Math.min(1 + Math.floor(heroComboCount / 3), 4)
  let appliedAny = false
  let justRemoved = false
  if (tris.length) {
    const neighbors = [
      [0, 0],
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [1, 1],
      [-1, -1],
      [1, -1],
      [-1, 1],
      [2, 0],
      [-2, 0],
      [0, 2],
      [0, -2],
      [2, 1],
      [2, -1],
      [-2, 1],
      [-2, -1],
      [1, 2],
      [-1, 2],
      [1, -2],
      [-1, -2]
    ]
    // Randomize crack shapes per click, but always remove the clicked cell first.
    const extraNeighbors = neighbors.slice(1)
    for (let i = extraNeighbors.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      const temp = extraNeighbors[i]
      extraNeighbors[i] = extraNeighbors[j]
      extraNeighbors[j] = temp
    }
    const orderedNeighbors = [[0, 0], ...extraNeighbors]
    let removedCount = 0
    for (const [du, dv] of orderedNeighbors) {
      if (removedCount >= cellsToRemove) break
      const uu = u + du
      const vv = v + dv
      if (uu < 0 || vv < 0 || uu >= segments || vv >= segments) continue
      const k2 = `${uu}_${vv}`
      if (cellSet?.has(k2)) continue
      const tris2 = map?.get(k2)
      if (!tris2) continue
      cellSet?.add(k2)
      heroRemovedCells += 1
      justRemoved = true
      removedCount += 1
      for (const tIndex of tris2) {
        const i0 = tIndex * 3
        damage[i0] = Math.max(damage[i0], HERO_CHIP_REMOVE)
        damage[i0 + 1] = Math.max(damage[i0 + 1], HERO_CHIP_REMOVE)
        damage[i0 + 2] = Math.max(damage[i0 + 2], HERO_CHIP_REMOVE)
      }
    }
    appliedAny = true
  } else {
    let closestIndex = -1
    let closestDist = Infinity
    for (let i = 0; i < posAttr.count; i++) {
      const ix = i * 3
      const dx = basePositions[ix] - heroChipPointLocal.x
      const dy = basePositions[ix + 1] - heroChipPointLocal.y
      const dz = basePositions[ix + 2] - heroChipPointLocal.z
      const dist = dx * dx + dy * dy + dz * dz
      if (dist < closestDist) {
        closestDist = dist
        closestIndex = i
      }
    }
    if (closestIndex >= 0) {
      const triBase = Math.floor(closestIndex / 3) * 3
      damage[triBase] = Math.max(damage[triBase], HERO_CHIP_REMOVE)
      damage[triBase + 1] = Math.max(damage[triBase + 1], HERO_CHIP_REMOVE)
      damage[triBase + 2] = Math.max(damage[triBase + 2], HERO_CHIP_REMOVE)
      appliedAny = true
    }
  }
  if (appliedAny) {
    const worldPos = heroChipPointLocal.clone()
    const worldNormal = heroChipNormalLocal.clone()
    heroCube.localToWorld(worldPos)
    worldNormal.transformDirection(heroCube.matrixWorld)
    spawnHeroDebris(worldPos, worldNormal, 0.25, 0xffffff)
    triggerHeroShake(0.025)
  }
}

function createHeroSparks() {
  if (heroSparkGroup) return
  heroSparkGroup = new THREE.Group()
  heroSparkGroup.renderOrder = 12
  scene.add(heroSparkGroup)
  const geom = new THREE.PlaneGeometry(0.05, 0.015)
  for (let i = 0; i < 40; i++) {
    const mat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    })
    const mesh = new THREE.Mesh(geom, mat)
    mesh.visible = false
    heroSparkGroup.add(mesh)
    heroSparks.push({
      mesh,
      life: 0,
      maxLife: 0,
      velocity: new THREE.Vector3()
    })
  }
}

function registerHeroChip() {
  const now = clock.getElapsedTime()
  if (now - heroLastChipTime > HERO_COMBO_WINDOW) heroComboCount = 0
  heroComboCount += 1
  heroLastChipTime = now
  heroComboDecayUntil = now + HERO_COMBO_WINDOW
}

function createHeroDebris() {
  if (heroDebrisGroup) return
  heroDebrisGroup = new THREE.Group()
  heroDebrisGroup.renderOrder = 6
  scene.add(heroDebrisGroup)
  const cubeGeom = new THREE.BoxGeometry(0.03, 0.03, 0.03)
  for (let i = 0; i < 80; i++) {
    const mat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    })
    const mesh = new THREE.Mesh(cubeGeom, mat)
    mesh.visible = false
    heroDebrisGroup.add(mesh)
    heroDebris.push({
      mesh,
      life: 0,
      maxLife: 0,
      velocity: new THREE.Vector3(),
      spin: new THREE.Vector3()
    })
  }
}

function spawnHeroDebris(position, normal, intensity = 1, color = 0xffffff) {
  createHeroDebris()
  const count = Math.round(THREE.MathUtils.clamp(3 + intensity * 6, 4, 10))
  for (let i = 0; i < count; i++) {
    const piece = heroDebris[i + Math.floor(Math.random() * heroDebris.length)]
    if (!piece) continue
    const dir = normal
      .clone()
      .multiplyScalar(THREE.MathUtils.randFloat(0.5, 1.2))
      .add(new THREE.Vector3(
        THREE.MathUtils.randFloat(-0.6, 0.6),
        THREE.MathUtils.randFloat(-0.4, 0.4),
        THREE.MathUtils.randFloat(-0.6, 0.6)
      ))
      .normalize()
    const speed = THREE.MathUtils.randFloat(0.6, 1.4) * intensity
    piece.velocity.copy(dir.multiplyScalar(speed))
    piece.velocity.y -= THREE.MathUtils.randFloat(0.2, 0.6)
    piece.spin.set(
      THREE.MathUtils.randFloat(-4, 4),
      THREE.MathUtils.randFloat(-4, 4),
      THREE.MathUtils.randFloat(-4, 4)
    )
    piece.life = THREE.MathUtils.randFloat(0.8, 1.6)
    piece.maxLife = piece.life
    piece.mesh.position.copy(position)
    piece.mesh.rotation.set(
      THREE.MathUtils.randFloat(0, Math.PI),
      THREE.MathUtils.randFloat(0, Math.PI),
      THREE.MathUtils.randFloat(0, Math.PI)
    )
    piece.mesh.material.color.setHex(color)
    piece.mesh.visible = true
    piece.mesh.material.opacity = 0.9
  }
}

function updateHeroDebris(delta) {
  if (!heroDebrisGroup) return
  const gravity = -2.4
  let anyVisible = false
  for (const piece of heroDebris) {
    if (piece.life <= 0) {
      if (piece.mesh.visible) piece.mesh.visible = false
      continue
    }
    piece.life -= delta
    piece.velocity.y += gravity * delta
    piece.mesh.position.addScaledVector(piece.velocity, delta)
    piece.mesh.rotation.x += piece.spin.x * delta
    piece.mesh.rotation.y += piece.spin.y * delta
    piece.mesh.rotation.z += piece.spin.z * delta
    piece.mesh.material.opacity = Math.max(0, piece.life / piece.maxLife)
    anyVisible = true
  }
  heroDebrisGroup.visible = anyVisible
}

function spawnHeroSparks(strength = 1) {
  if (!heroCube) return
  createHeroSparks()
  const count = Math.round(THREE.MathUtils.clamp(strength * 10, 6, 16))
  const right = new THREE.Vector3(1, 0, 0)
  const up = new THREE.Vector3(0, 1, 0)
  const forward = new THREE.Vector3(0, 0, 1)
  const basePos = heroCube.position.clone()
  for (let i = 0; i < count; i++) {
    const spark = heroSparks[heroSparkIndex % heroSparks.length]
    heroSparkIndex += 1
    const dir = right
      .clone()
      .multiplyScalar(THREE.MathUtils.randFloat(-1, 1))
      .add(up.clone().multiplyScalar(THREE.MathUtils.randFloat(-1, 1)))
      .add(forward.clone().multiplyScalar(THREE.MathUtils.randFloat(-0.4, 0.4)))
      .normalize()
    const speed = THREE.MathUtils.randFloat(0.8, 1.6) * strength
    spark.velocity.copy(dir.multiplyScalar(speed))
    spark.life = THREE.MathUtils.randFloat(0.25, 0.45)
    spark.maxLife = spark.life
    spark.mesh.position.copy(basePos)
    spark.mesh.rotation.z = Math.atan2(dir.y, dir.x)
    spark.mesh.visible = true
    spark.mesh.material.opacity = 1
  }
}

function updateHeroSparks(delta) {
  if (!heroSparkGroup) return
  let anyVisible = false
  for (const spark of heroSparks) {
    if (spark.life <= 0) {
      if (spark.mesh.visible) spark.mesh.visible = false
      continue
    }
    spark.life -= delta
    spark.velocity.multiplyScalar(0.9)
    spark.mesh.position.addScaledVector(spark.velocity, delta)
    spark.mesh.lookAt(camera.position)
    spark.mesh.material.opacity = Math.max(0, spark.life / spark.maxLife)
    anyVisible = true
  }
  heroSparkGroup.visible = anyVisible
}

function launchHeroCube({ mode = 'dramatic' } = {}) {
  createHeroCube()
  createHeroClosePlane()
  if (introActive) {
    introActive = false
    camera.position.copy(cameraBasePosition)
    controls.target.copy(anchor.name)
    controls.update()
  }
  heroMode = mode
  heroSquishActive = false
  heroShakeActive = false
  heroBasePos.copy(anchor.name)
  heroCube.visible = true
  heroCube.position.copy(heroBasePos)
  heroCube.rotation.copy(heroSpawnBaseRotation)
  heroCube.rotation.x += HERO_SPAWN_TILT.x
  heroCube.rotation.y += HERO_SPAWN_TILT.y
  heroCube.rotation.z += HERO_SPAWN_TILT.z
  if (nameHitPlane) nameHitPlane.visible = false
  if (mode === 'inline') {
    heroState = 'active'
    return
  }

  if (mode === 'shoot') {
    captureControlsDefaults()
    const rise = mobileQuery.matches ? HERO_RISE_MOBILE : HERO_RISE
    heroTargetPos.copy(anchor.name).add(new THREE.Vector3(0, rise, 0))
    heroTransitionStart = clock.getElapsedTime()
    heroState = 'spawning'
    heroCameraFromPosition.copy(camera.position)
    heroCameraFromTarget.copy(controls.target)
    heroSpinStartRot.copy(heroCube.rotation)
    applyHeroControls()
    return
  }

  createHeroDonuts()
  captureControlsDefaults()
  const rise = mobileQuery.matches ? HERO_RISE_MOBILE : HERO_RISE
  heroTargetPos.copy(anchor.name).add(new THREE.Vector3(0, rise, 0))
  heroTransitionStart = clock.getElapsedTime()
  heroState = 'spawning'
  heroCameraFromPosition.copy(camera.position)
  heroCameraFromTarget.copy(controls.target)
  heroSpinStartRot.copy(heroCube.rotation)
  applyHeroControls()
  if (heroDonutMesh) heroDonutMesh.visible = true
  if (heroDonutMesh) heroDonutMesh.position.copy(heroBasePos)
  if (donutMesh) donutMesh.visible = false
  if (linkMeshes.length) {
    for (const mesh of linkMeshes) mesh.visible = false
  }
  if (nameMeshRef) nameMeshRef.visible = false
  if (subtitleMeshRef) subtitleMeshRef.visible = false
}

function closeHeroCube() {
  if (!heroCube || heroState === 'idle' || heroState === 'closing') return
  if (heroMode === 'about') return
  heroState = 'closing'
  heroTransitionStart = clock.getElapsedTime()
  heroCameraFromPosition.copy(camera.position)
  heroCameraFromTarget.copy(controls.target)
}

function setupHeroCubeControls() {
  renderer.domElement.addEventListener('pointerdown', (e) => {
    if (!heroCube || heroState === 'idle') return
    if (
      (aboutState !== 'closed' && heroMode !== 'about') ||
      galleryState !== 'closed' ||
      reelState !== 'closed' ||
      videosState !== 'closed'
    ) {
      return
    }
    setPointerFromEvent(e)
    let hits = raycaster.intersectObject(heroCube, false)
    let hit = hits.length ? hits[0] : null
    if (!hit && heroHitMesh) {
      hits = raycaster.intersectObject(heroHitMesh, false)
      if (hits.length) hit = hits[0]
    }
    if (!hit) return
    heroDragActive = true
    heroDragMoved = false
    heroSpinStartRot.copy(heroCube.rotation)
    heroDragPointer.set(e.clientX, e.clientY)
    heroAngularVelocity.set(0, 0)
    heroSquishActive = false
    heroShakeActive = false
    heroPointerDownHit = hit
    heroCube.userData.targetScale = 1
    controls.enableRotate = false
    controls.enablePan = false
    controls.enableZoom = false
  })

  window.addEventListener('pointermove', (e) => {
    if (!heroDragActive || !heroCube) return
    const dx = e.clientX - heroDragPointer.x
    const dy = e.clientY - heroDragPointer.y
    heroDragPointer.set(e.clientX, e.clientY)
    if (Math.abs(dx) + Math.abs(dy) > 2) heroDragMoved = true
    const dirX = -1
    const dirY = 1
    heroCube.rotation.y += dx * HERO_DRAG_ROT_SPEED * dirX
    heroCube.rotation.x += dy * HERO_DRAG_ROT_SPEED * dirY
    heroAngularVelocity.set(dy * 0.0008 * dirY, dx * 0.0008 * dirX)
  })

  window.addEventListener('pointerup', () => {
    if (!heroDragActive) return
    heroDragActive = false
    heroDragLastUp = performance.now()
    if (!heroDragMoved) {
      // Mining/destroy interactivity disabled.
    }
    heroPointerDownHit = null
    window.setTimeout(() => {
      heroDragMoved = false
    }, 200)
    controls.enableRotate = true
    controls.enableZoom = true
  })
}

function setupRatedControls() {
  renderer.domElement.addEventListener('pointerdown', (e) => {
    if (!ratedRoot || !ratedReady) return
    if (aboutState !== 'closed' || galleryState !== 'closed' || reelState !== 'closed' || videosState !== 'closed') {
      return
    }
    setPointerFromEvent(e)
    const hit = getIntersect()
    if (!hit || !getRatedRoot(hit.object)) return
    ratedDragActive = true
    ratedDragMoved = false
    ratedDragPointer.set(e.clientX, e.clientY)
    controls.enableRotate = false
    controls.enablePan = false
    controls.enableZoom = false
  })

  window.addEventListener('pointermove', (e) => {
    if (!ratedDragActive || !ratedRoot) return
    const dx = e.clientX - ratedDragPointer.x
    const dy = e.clientY - ratedDragPointer.y
    ratedDragPointer.set(e.clientX, e.clientY)
    if (Math.abs(dx) + Math.abs(dy) > 2) ratedDragMoved = true
    const rotSpeed = 0.005
    ratedRoot.rotation.y += dx * rotSpeed
    ratedRoot.rotation.x += dy * rotSpeed
    ratedAngularVelocity.set(dy * 0.0008, dx * 0.0008)
  })

  window.addEventListener('pointerup', () => {
    if (!ratedDragActive) return
    ratedDragActive = false
    ratedDragLastUp = performance.now()
    window.setTimeout(() => {
      ratedDragMoved = false
    }, 200)
    controls.enableRotate = true
  })
}


function buildNameTextGeometry({ font, text, size, depth, quality }) {
  const geo = new TextGeometry(text, {
    font,
    size,
    depth,
    curveSegments: quality.curveSegments,
    bevelEnabled: true,
    bevelThickness: quality.bevelThickness,
    bevelSize: quality.bevelSize,
    bevelOffset: 0,
    bevelSegments: quality.bevelSegments
  })
  geo.center()
  return geo
}

function addInteractiveText({ font, label, position, size = 0.35, depth = 0.14 }) {
  const quality = getSubtitleQuality(mobileQuery.matches)
  const geo = new TextGeometry(label, {
    font,
    size,
    depth,
    curveSegments: quality.curveSegments,
    bevelEnabled: true,
    bevelThickness: quality.bevelThickness,
    bevelSize: quality.bevelSize,
    bevelOffset: 0,
    bevelSegments: quality.bevelSegments
  })
  geo.center()

  const mat = linkBaseMaterial.clone()
  const mesh = new THREE.Mesh(geo, mat)
  mesh.position.copy(position)

  mesh.userData.baseScale = mesh.scale.clone()
  mesh.userData.targetScale = 1
  mesh.userData.label = label
  mesh.userData.basePos = position.clone()
  mesh.userData.blocksDonutHover = true
  mesh.rotation.copy(linkTilt)
  mesh.userData.baseRotation = mesh.rotation.clone()
  const href = linkTargets[label]
  if (href) mesh.userData.href = href
  if (label === 'about') mesh.userData.clickable = true
  mesh.userData.spin = {
    active: false,
    baseRotX: mesh.rotation.x,
    startRotX: 0,
    t0: 0,
    duration: 0.65
  }
  mesh.userData.onClick = () => {
    if (label === '2025 reel') {
      window.open(REEL_URL, '_blank', 'noopener')
      return
    }
    if (label === 'about') {
      openAboutCard()
      return
    }
    if (label === 'videos') {
      openVideosScene()
      return
    }
    if (href) {
      if (window.top === window) {
        window.location.assign(href)
      } else {
        window.parent.postMessage({ type: 'nav', href }, parentOrigin)
      }
      return
    }
    const spin = mesh.userData.spin
    const delta = mesh.rotation.x - spin.baseRotX
    spin.startRotX = spin.baseRotX + THREE.MathUtils.euclideanModulo(delta, Math.PI * 2)
    spin.t0 = clock.getElapsedTime()
    spin.active = true
  }

  scene.add(mesh)
  interactive.push(mesh)

  floatingTexts.push({
    mesh,
    basePos: position.clone(),
    speed: rand(0.7, 1.1),
    amp: rand(0.06, 0.12),
    phase: rand(0, Math.PI * 2),
    rotAmp: 0.06
  })

  return mesh
}

/**
 * Cubes
 */
const donuts = []
let donutMesh = null
const donutDummy = new THREE.Object3D()
const images = []
const CUBE_COUNT = mobileQuery.matches ? 120 : 135
const IMAGE_COUNT = 20

const CENTER_CLEAR_RADIUS = 3.2
const TEXT_CLEAR_RADIUS = 1.2
const DONUT_MIN_DISTANCE = 0.28
const NAME_COLOR_RADIUS = 2.2

const SPAWN_RANGE_XY = mobileQuery.matches ? 6 : 7
const SPAWN_RANGE_Z = mobileQuery.matches ? 6 : 7

function pickDifferentMatcap() {
  if (donutMatcaps.length < 2) return donutMatcapIndex
  let next = donutMatcapIndex
  while (next === donutMatcapIndex) {
    next = Math.floor(Math.random() * donutMatcaps.length)
  }
  donutMatcapIndex = next
  return donutMatcapIndex
}

function resetSpread(items, getOffset) {
  for (const item of items) getOffset(item).set(0, 0, 0)
}

function spreadFrom(point, items, getBasePos, getOffset) {
  for (const item of items) {
    const base = getBasePos(item)
    const dir = new THREE.Vector3().subVectors(base, point)
    const len = Math.max(dir.length(), 0.001)
    dir.normalize()

    const strength = THREE.MathUtils.clamp(1.6 / (len * len), 0, 0.35)
    getOffset(item).copy(dir.multiplyScalar(strength))
  }
}

function isValidSpawnPosition(pos) {
  if (pos.length() < CENTER_CLEAR_RADIUS) return false

  if (dist(pos, anchor.name) < TEXT_CLEAR_RADIUS) return false
  if (dist(pos, anchor.photos) < TEXT_CLEAR_RADIUS) return false
  if (dist(pos, anchor.videos) < TEXT_CLEAR_RADIUS) return false
  if (dist(pos, anchor.about) < TEXT_CLEAR_RADIUS) return false
  if (dist(pos, anchor.contact) < TEXT_CLEAR_RADIUS) return false

  if (textBlockers.length) {
    for (const blocker of textBlockers) {
      const camToText = new THREE.Vector3().subVectors(blocker.center, cameraBasePosition)
      const textDist = camToText.length()
      if (textDist < 0.001) continue
      const dir = camToText.normalize()
      const camToPos = new THREE.Vector3().subVectors(pos, cameraBasePosition)
      const proj = camToPos.dot(dir)
      if (proj <= 0 || proj >= textDist) continue
      const perpSq = Math.max(0, camToPos.lengthSq() - proj * proj)
      const clearance = blocker.radius + INTRO_TEXT_BLOCKER_PADDING
      if (perpSq <= clearance * clearance) return false
    }
  }

  if (ratedSpawnReady) {
    if (dist(pos, ratedSpawnTarget) < ratedBlockerRadius * 2.2) return false
    const camToRated = new THREE.Vector3().subVectors(ratedSpawnTarget, ratedSpawnCameraPos)
    const ratedDist = camToRated.length()
    if (ratedDist > 0.001) {
      const dir = camToRated.normalize()
      const camToPos = new THREE.Vector3().subVectors(pos, ratedSpawnCameraPos)
      const proj = camToPos.dot(dir)
      if (proj > 0 && proj < ratedDist) {
        const perpSq = Math.max(0, camToPos.lengthSq() - proj * proj)
        const clearance = ratedBlockerRadius + 0.08
        if (perpSq <= clearance * clearance) return false
      }
    }
  }

  for (const d of donuts) {
    if (dist(pos, d.basePos) < DONUT_MIN_DISTANCE) return false
  }
  for (const img of images) {
    if (dist(pos, img.userData.basePos) < DONUT_MIN_DISTANCE) return false
  }
  return true
}

function sampleDonutPosition() {
  return new THREE.Vector3(
    (Math.random() - 0.5) * SPAWN_RANGE_XY,
    (Math.random() - 0.5) * SPAWN_RANGE_XY,
    (Math.random() - 0.5) * SPAWN_RANGE_Z
  )
}

function spawnDonuts() {
  const donutGeometry = new THREE.BoxGeometry(0.35, 0.35, 0.35)
  donutMesh = new THREE.InstancedMesh(donutGeometry, donutBaseMaterial, CUBE_COUNT)
  donutMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)

  let tries = 0
  const maxTries = CUBE_COUNT * 200

  while (donuts.length < CUBE_COUNT && tries < maxTries) {
    tries++
    const pos = sampleDonutPosition()
    if (!isValidSpawnPosition(pos)) continue

    const rotation = new THREE.Euler(Math.random() * Math.PI, Math.random() * Math.PI, 0)
    const s = THREE.MathUtils.randFloat(0.45, 1.0)
    const scale = new THREE.Vector3(s, s, s)

    donuts.push({
      basePos: pos.clone(),
      pos: pos.clone(),
      spreadOffset: new THREE.Vector3(0, 0, 0),
      baseScale: scale.clone(),
      scale: scale.clone(),
      rotation,
      spin: {
      active: false,
      baseRotX: rotation.x,
      startRotX: 0,
      t0: 0,
      duration: 0.65
    }
    })

    donutDummy.position.copy(pos)
    donutDummy.rotation.copy(rotation)
    donutDummy.scale.copy(scale)
    donutDummy.updateMatrix()
    donutMesh.setMatrixAt(donuts.length - 1, donutDummy.matrix)
  }

  donutMesh.count = donuts.length
  donutMesh.instanceMatrix.needsUpdate = true
  scene.add(donutMesh)
  interactive.push(donutMesh)
  resetSpread(donuts, (item) => item.spreadOffset)

  if (donuts.length < CUBE_COUNT) {
    console.warn(
      `Only spawned ${donuts.length}/${CUBE_COUNT} cubes. ` +
      `Increase SPAWN_RANGE_* or lower CLEAR/DIST thresholds to fit more.`
    )
  }
}

function spawnImages() {
  const imageTexture = textureLoader.load('/images/1.webp')
  imageTexture.colorSpace = THREE.SRGBColorSpace
  const imageGeometry = new THREE.PlaneGeometry(0.9, 0.9)
  const imageMaterial = new THREE.MeshBasicMaterial({
    map: imageTexture,
    transparent: true
  })

  let tries = 0
  const maxTries = IMAGE_COUNT * 200

  while (images.length < IMAGE_COUNT && tries < maxTries) {
    tries++
    const pos = sampleDonutPosition()
    if (!isValidSpawnPosition(pos)) continue

    const imageMesh = new THREE.Mesh(imageGeometry, imageMaterial.clone())
    imageMesh.position.copy(pos)
    imageMesh.rotation.set(0, THREE.MathUtils.randFloat(-0.45, 0.45), 0)

    const s = THREE.MathUtils.randFloat(0.6, 0.95)
    imageMesh.scale.set(s, s, s)

    imageMesh.userData.baseScale = imageMesh.scale.clone()
    imageMesh.userData.basePos = imageMesh.position.clone()
    imageMesh.userData.spreadOffset = new THREE.Vector3(0, 0, 0)
    imageMesh.userData.targetScale = 1

    imageMesh.userData.onHoverIn = () =>
      spreadFrom(
        imageMesh.userData.basePos,
        images,
        (item) => item.userData.basePos,
        (item) => item.userData.spreadOffset
      )
    imageMesh.userData.onHoverOut = () =>
      resetSpread(images, (item) => item.userData.spreadOffset)

    scene.add(imageMesh)
    images.push(imageMesh)
    interactive.push(imageMesh)
  }

  resetSpread(images, (item) => item.userData.spreadOffset)

  if (images.length < IMAGE_COUNT) {
    console.warn(
      `Only spawned ${images.length}/${IMAGE_COUNT} images. ` +
      `Increase SPAWN_RANGE_* or lower CLEAR/DIST thresholds to fit more.`
    )
  }
}

/**
 * Load font + build scene
 */
fontLoader.load('/fonts/helvetiker_regular.typeface.json', (font) => {
  uiFont = font
  updateLinkAnchors()
  // Main name text
  const nameQuality = getTextQuality(mobileQuery.matches)
  const nameGeo = buildNameTextGeometry({
    font,
    text: nameText,
    size: 0.5,
    depth: 0.2,
    quality: nameQuality
  })

  const nameMaterial = baseMaterial.clone()
  const nameMesh = new THREE.Mesh(nameGeo, nameMaterial)
  nameMesh.position.copy(anchor.name)
  scene.add(nameMesh)
  textMeshes.push(nameMesh)
  registerStartupText(nameMesh)
  const nameBounds = new THREE.Box3().setFromObject(nameMesh)
  const nameSize = new THREE.Vector3()
  nameBounds.getSize(nameSize)
  const hitWidth = nameSize.x * 1.05
  const hitHeight = nameSize.y * 1.1
  const nameHitGeo = new THREE.PlaneGeometry(hitWidth, hitHeight)
  const nameHitMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthTest: false })
  nameHitPlane = new THREE.Mesh(nameHitGeo, nameHitMat)
  nameHitPlane.position.set(0, 0, 0.02)
  nameHitPlane.userData.forwardHit = true
  nameMesh.add(nameHitPlane)
  interactive.push(nameHitPlane)

  // Subtitle under name
  const subtitleQuality = getSubtitleQuality(mobileQuery.matches)
  const subtitleGeo = new TextGeometry('director - editor - creative', {
    font,
    size: 0.22,
    depth: 0.1,
    curveSegments: subtitleQuality.curveSegments,
    bevelEnabled: true,
    bevelThickness: subtitleQuality.bevelThickness,
    bevelSize: subtitleQuality.bevelSize,
    bevelOffset: 0,
    bevelSegments: subtitleQuality.bevelSegments
  })
  subtitleGeo.center()
  const subtitleMesh = new THREE.Mesh(subtitleGeo, baseMaterial.clone())
  subtitleMesh.position.copy(anchor.name)
  subtitleMesh.position.y -= 0.55
  scene.add(subtitleMesh)
  textMeshes.push(subtitleMesh)
  registerStartupText(subtitleMesh)
  subtitleMesh.userData.baseScale = subtitleMesh.scale.clone()
  subtitleMesh.userData.targetScale = 1
  subtitleMesh.userData.blocksDonutHover = false
  subtitleMesh.userData.noStartupMove = true
  subtitleMesh.userData.onClick = () => {
    if (!subtitleMeshRef) return
    subtitleSpin.active = true
    const delta = subtitleMeshRef.rotation.x - subtitleSpin.baseRotX
    subtitleSpin.startRotX = subtitleSpin.baseRotX + THREE.MathUtils.euclideanModulo(delta, Math.PI * 2)
    subtitleSpin.t0 = clock.getElapsedTime()
  }
  interactive.push(subtitleMesh)

  // Make name mesh interactive for hover/click wireframe toggle
  nameMesh.userData.baseScale = nameMesh.scale.clone()
  nameMesh.userData.targetScale = 1
  nameMesh.userData.interactEnabled = false
  nameMesh.userData.clickable = false
  nameMesh.userData.blocksDonutHover = false
  nameMesh.userData.hoverSticky = true
  nameMesh.userData.onHoverIn = () => {
    nameMesh.userData.targetScale = 1.35
  }
  nameMesh.userData.onHoverOut = () => {
    nameMesh.userData.targetScale = 1
  }
  nameMesh.userData.onClick = () => {
    if (performance.now() < nameClickLockUntil) return
    if (!nameMesh.userData.interactEnabled) enableNameInteraction()
    if (heroState === 'idle') launchHeroCube({ mode: 'shoot' })
  }
  // Not a link, so no href (cursor stays default)
  interactive.push(nameMesh)

  nameMeshRef = nameMesh
  subtitleMeshRef = subtitleMesh
  subtitleSpin.baseRotX = subtitleMesh.rotation.x


  // Link texts
  linkMeshes.push(addInteractiveText({ font, label: '2025 reel', position: anchor.contact }))
  linkMeshes.push(addInteractiveText({ font, label: 'videos', position: anchor.videos }))
  linkMeshes.push(addInteractiveText({ font, label: 'about', position: anchor.about }))
  linkMeshes.push(addInteractiveText({ font, label: 'photos', position: anchor.photos }))
  textMeshes.push(...linkMeshes)
  for (const mesh of linkMeshes) registerStartupText(mesh)
  fitTextToViewport()
  updateTextBlockers()
  computeRatedSpawnTarget()
  loadRatedModel()

  // Donuts
  spawnDonuts()
  if (!mobileQuery.matches || startupSpinPending) {
    runStartupSpin()
  }
  // Images (disabled)
  // spawnImages()

  fontReady = true
  warmupRender(6)
  tryHideMobileLoader()
  if (!mobileQuery.matches) startIntroMotion()
})

window.enableAbout3DText = () => setAbout3DTextEnabled(true)
window.disableAbout3DText = () => setAbout3DTextEnabled(false)
window.getAbout3DTextStatus = () => ({
  requested: aboutPrefer3DText,
  active: aboutText3DEnabled
})

/**
 * Animate
 */
const clock = new THREE.Clock()

function tick() {
  const t = clock.getElapsedTime()
  const delta = t - lastTickTime
  lastTickTime = t

  if (aboutState === 'opening') {
    const u = (t - aboutTransitionStart) / ABOUT_TRANSITION_SEC
    const eased = easeOutCubic(Math.max(0, Math.min(1, u)))
    camera.position.lerpVectors(controlsDefaults.position, aboutCameraPos, eased)
    controls.target.lerpVectors(controlsDefaults.target, aboutCameraTarget, eased)
    if (u >= 1) aboutState = 'open'
  } else if (aboutState === 'closing') {
    const u = (t - aboutTransitionStart) / ABOUT_TRANSITION_SEC
    const eased = easeInOutCubic(Math.max(0, Math.min(1, u)))
    camera.position.lerpVectors(aboutCloseFromPosition, controlsDefaults.position, eased)
    controls.target.lerpVectors(aboutCloseFromTarget, controlsDefaults.target, eased)
    if (aboutPanelPlane) {
      const baseScale = aboutPanelPlane.userData.baseScale ?? aboutPanelPlane.scale
      const shrink = Math.max(0.001, 1 - eased)
      aboutPanelPlane.scale.copy(baseScale).multiplyScalar(shrink)
      if (aboutPanelPlane.material) aboutPanelPlane.material.opacity = 1 - eased
      if (aboutPanelBackPlane?.material) aboutPanelBackPlane.material.opacity = (1 - eased) * 0.9
      if (aboutPanelImagePlane?.material) aboutPanelImagePlane.material.opacity = 1 - eased
      if (aboutPanelContentPlane?.material) aboutPanelContentPlane.material.opacity = 1 - eased
    }
    if (u >= 1) {
      aboutState = 'closed'
      if (aboutPanelPlane) {
        aboutPanelPlane.visible = false
        if (aboutPanelPlane.material) aboutPanelPlane.material.opacity = 1
        if (aboutPanelBackPlane?.material) aboutPanelBackPlane.material.opacity = 0.9
        if (aboutPanelImagePlane?.material) aboutPanelImagePlane.material.opacity = 1
        if (aboutPanelContentPlane?.material) aboutPanelContentPlane.material.opacity = 1
        if (aboutPanelPlane.userData.baseScale) aboutPanelPlane.scale.copy(aboutPanelPlane.userData.baseScale)
      }
      restoreControls()
    }
  }

  if (galleryState === 'opening') {
    const u = (t - galleryTransitionStart) / GALLERY_TRANSITION_SEC
    const eased = easeOutCubic(Math.max(0, Math.min(1, u)))
    camera.position.lerpVectors(controlsDefaults.position, galleryCameraPosition, eased)
    controls.target.lerpVectors(controlsDefaults.target, galleryPlaneTarget, eased)
    if (u >= 1) galleryState = 'open'
  } else if (galleryState === 'closing') {
    const u = (t - galleryTransitionStart) / GALLERY_TRANSITION_SEC
    const eased = easeOutCubic(Math.max(0, Math.min(1, u)))
    camera.position.lerpVectors(galleryCloseFromPosition, controlsDefaults.position, eased)
    controls.target.lerpVectors(galleryCloseFromTarget, controlsDefaults.target, eased)
    if (u >= 1) {
      galleryState = 'closed'
      if (galleryPlane) galleryPlane.visible = false
      restoreControls()
    }
  }

  if (reelState === 'opening') {
    const u = (t - reelTransitionStart) / REEL_TRANSITION_SEC
    const eased = easeOutCubic(Math.max(0, Math.min(1, u)))
    camera.position.lerpVectors(controlsDefaults.position, REEL_CAMERA_POSITION, eased)
    controls.target.lerpVectors(controlsDefaults.target, REEL_TARGET, eased)
    if (u >= 1) reelState = 'open'
  } else if (reelState === 'closing') {
    const u = (t - reelTransitionStart) / REEL_TRANSITION_SEC
    const eased = easeOutCubic(Math.max(0, Math.min(1, u)))
    camera.position.lerpVectors(reelCloseFromPosition, controlsDefaults.position, eased)
    controls.target.lerpVectors(reelCloseFromTarget, controlsDefaults.target, eased)
    if (u >= 1) {
      reelState = 'closed'
      if (reelPlane) reelPlane.visible = false
      restoreControls()
    }
  }

  if (videosState === 'opening') {
    const u = (t - videosTransitionStart) / VIDEOS_TRANSITION_SEC
    const eased = easeOutCubic(Math.max(0, Math.min(1, u)))
    camera.position.lerpVectors(controlsDefaults.position, videosCameraPosition, eased)
    controls.target.lerpVectors(controlsDefaults.target, videosTarget, eased)
    if (u >= 1) videosState = 'open'
  } else if (videosState === 'closing') {
    const u = (t - videosTransitionStart) / VIDEOS_TRANSITION_SEC
    const eased = easeOutCubic(Math.max(0, Math.min(1, u)))
    camera.position.lerpVectors(videosCloseFromPosition, controlsDefaults.position, eased)
    controls.target.lerpVectors(videosCloseFromTarget, controlsDefaults.target, eased)
    if (u >= 1) {
      videosState = 'closed'
      if (portfolioGroup) portfolioGroup.visible = false
      if (heroCube && heroState !== 'idle') heroCube.visible = true
      if (linkMeshes.length) {
        for (const mesh of linkMeshes) mesh.visible = true
      }
      restoreControls()
    }
  }

  if (videoDetailState === 'opening') {
    const u = (t - videoDetailTransitionStart) / VIDEO_DETAIL_TRANSITION_SEC
    const eased = easeOutCubic(Math.max(0, Math.min(1, u)))
    if (videoDetailPlane) {
      videoDetailPlane.material.opacity = eased
      if (u >= 1) videoDetailPlane.material.opacity = 1
    }
    if (u >= 1) videoDetailState = 'open'
  } else if (videoDetailState === 'closing') {
    const u = (t - videoDetailTransitionStart) / VIDEO_DETAIL_TRANSITION_SEC
    const eased = easeOutCubic(Math.max(0, Math.min(1, u)))
    if (videoDetailPlane) {
      videoDetailPlane.material.opacity = 1 - eased
      if (u >= 1) {
        videoDetailPlane.visible = false
        videoDetailPlane.material.opacity = 1
      }
    }
    if (u >= 1) videoDetailState = 'closed'
  }

  for (const mesh of startupTextMeshes) {
    const spin = mesh.userData.startupSpin
    if (!spin?.active) continue
    if (mesh === subtitleMeshRef && subtitleSpin.active) continue
    const u = (t - spin.t0) / spin.duration
    if (u >= 1) {
      mesh.rotation.x = spin.baseRotX
      mesh.userData.startupOffset.set(0, 0, 0)
      spin.active = false
      if (mesh === nameMeshRef && !nameInteractEnabled) enableNameInteraction()
    } else if (u >= 0) {
      const eased = easeOutCubic(Math.max(0, Math.min(1, u)))
      mesh.rotation.x = spin.startRotX + (Math.PI * 2) * eased
      mesh.userData.startupOffset.copy(spin.startOffset).multiplyScalar(1 - eased)
    }
  }

  if (heroCube && heroState !== 'idle') {
    if (heroState === 'spawning') {
      const duration = heroMode === 'shoot' ? HERO_TRANSITION_SEC_SHOOT : HERO_TRANSITION_SEC
      const u = (t - heroTransitionStart) / duration
      const eased = easeOutCubic(Math.max(0, Math.min(1, u)))
      heroCube.position.lerpVectors(heroBasePos, heroTargetPos, eased)
      if (!heroDragActive) {
        heroCube.rotation.x = heroSpinStartRot.x + Math.PI * 2 * heroSpinTurns * eased
        heroCube.rotation.y = heroSpinStartRot.y + Math.PI * 1.4 * eased
      }
      const baseScale = heroCube.userData.baseScale ?? heroCube.scale
      heroCube.scale.copy(baseScale).multiplyScalar(Math.max(0.001, eased))
      if (heroMode === 'shoot' && mobileQuery.matches) {
        const scale = heroCube.scale.x
        const cubeWidth = 0.9 * scale
        const padding = 1.35
        const fovRad = THREE.MathUtils.degToRad(camera.fov)
        const needed = (cubeWidth * padding) / (2 * Math.tan(fovRad / 2) * camera.aspect)
        heroCameraTargetPos.copy(heroTargetPos).add(new THREE.Vector3(0, 0, Math.max(needed, HERO_CAMERA_OFFSET_SHOOT_MOBILE.z)))
      } else {
        const cameraOffset =
          heroMode === 'shoot'
            ? HERO_CAMERA_OFFSET_SHOOT
            : HERO_CAMERA_OFFSET
        heroCameraTargetPos.copy(heroTargetPos).add(cameraOffset)
      }
      heroCameraLagPos.lerpVectors(heroCameraFromPosition, heroCameraTargetPos, eased)
      camera.position.lerp(heroCameraLagPos, HERO_CAMERA_LAG)
      controls.target.lerpVectors(heroCameraFromTarget, heroTargetPos, eased)
      if (heroDonutMesh) heroDonutMesh.position.lerpVectors(heroBasePos, heroTargetPos, eased)
      if (u >= 1) {
        heroState = 'active'
        heroAngularVelocity.set(0, 0)
        heroCube.userData.baseRotation.copy(heroCube.rotation)
        if (heroCube.userData.baseScale) heroCube.scale.copy(heroCube.userData.baseScale)
        if (heroClosePlane) heroClosePlane.visible = true
      }
    }

    if (heroState === 'active') {
      controls.target.copy(heroTargetPos)
    }

    if (heroState === 'active' && !heroDragActive) {
      const baseRot = heroCube.userData.baseRotation
      if (baseRot) {
        heroAngularVelocity.x += (baseRot.x - heroCube.rotation.x) * HERO_ROTATION_SPRING
        heroAngularVelocity.y += (baseRot.y - heroCube.rotation.y) * HERO_ROTATION_SPRING
        heroAngularVelocity.multiplyScalar(HERO_ROTATION_DAMPING)
        heroCube.rotation.x += heroAngularVelocity.x
        heroCube.rotation.y += heroAngularVelocity.y
      }
    }

    if (heroShakeActive) {
      heroShakeTime = Math.max(0, heroShakeTime - delta)
      const k = heroShakeTime / HERO_SHAKE_DURATION
      const amp = heroShakeStrength * (k * k)
      heroShakeOffset.set(
        Math.sin(t * 36) * amp * 0.4,
        Math.cos(t * 30) * amp * 0.35,
        Math.sin(t * 22) * amp * 0.25
      )
      heroShakeRot.set(
        Math.sin(t * 28) * amp * 0.35,
        Math.cos(t * 33) * amp * 0.35,
        Math.sin(t * 24) * amp * 0.18
      )
      heroCube.position.copy(heroShakeBasePos).add(heroShakeOffset)
      heroCube.rotation.x = heroShakeBaseRot.x + heroShakeRot.x
      heroCube.rotation.y = heroShakeBaseRot.y + heroShakeRot.y
      heroCube.rotation.z = heroShakeBaseRot.z + heroShakeRot.z
      if (heroShakeTime <= 0.001) {
        heroShakeStrength = 0
        heroShakeActive = false
        heroCube.position.copy(heroShakeBasePos)
        heroCube.rotation.x = heroShakeBaseRot.x
        heroCube.rotation.y = heroShakeBaseRot.y
        heroCube.rotation.z = heroShakeBaseRot.z
      }
    }


    if (heroDonutMesh && heroDonuts.length) {
      for (let i = 0; i < heroDonuts.length; i++) {
        const donut = heroDonuts[i]
        donut.rotation.y += delta * donut.rotSpeed
        donut.rotation.x += delta * donut.rotSpeed * 0.6
        heroDonutDummy.position.copy(donut.basePos)
        heroDonutDummy.rotation.copy(donut.rotation)
        heroDonutDummy.scale.copy(donut.scale)
        heroDonutDummy.updateMatrix()
        heroDonutMesh.setMatrixAt(i, heroDonutDummy.matrix)
      }
      heroDonutMesh.instanceMatrix.needsUpdate = true
    }

    let scaleApplied = false
    const baseScale = heroCube.userData.baseScale ?? heroCube.scale
    const squishBaseScale = (heroSquishActive || heroDragActive) ? heroSquishBaseScale : baseScale
    const scaleVec = squishBaseScale.clone()

    if (heroSquishActive) {
      const u = (t - heroSquishStart) / HERO_SQUISH_DURATION
      if (u >= 1) {
        heroSquishActive = false
      } else {
        const damp = Math.exp(-heroSquishDamp * u)
        const wobble = Math.sin(u * Math.PI * heroSquishWobble) * damp
        const squash = heroSquishAmount * wobble
        scaleVec.set(
          squishBaseScale.x * (1 + squash * 0.8),
          squishBaseScale.y * (1 - squash * 1.1),
          squishBaseScale.z * (1 + squash * 0.8)
        )
        scaleApplied = true
      }
    }

    if (heroCube?.geometry?.attributes?.position && heroCube.userData.basePositions) {
      const posAttr = heroCube.geometry.attributes.position
      const basePositions = heroCube.userData.basePositions
      const damage = heroCube.userData.damage
      const triRemoved = heroCube.userData.triRemoved
      const triCount = Math.floor(posAttr.count / 3)
      for (let tIndex = 0; tIndex < triCount; tIndex++) {
        const i0 = tIndex * 3
        const i1 = i0 + 1
        const i2 = i0 + 2
        const d0 = damage?.[i0] ?? 0
        const d1 = damage?.[i1] ?? 0
        const d2 = damage?.[i2] ?? 0
        const removed = Math.max(d0, d1, d2) >= HERO_CHIP_REMOVE
        if (removed && triRemoved && !triRemoved[tIndex]) {
          triRemoved[tIndex] = 1
          const cx =
            (basePositions[i0 * 3] + basePositions[i1 * 3] + basePositions[i2 * 3]) / 3
          const cy =
            (basePositions[i0 * 3 + 1] + basePositions[i1 * 3 + 1] + basePositions[i2 * 3 + 1]) / 3
          const cz =
            (basePositions[i0 * 3 + 2] + basePositions[i1 * 3 + 2] + basePositions[i2 * 3 + 2]) / 3
          const v0 = new THREE.Vector3(
            basePositions[i0 * 3],
            basePositions[i0 * 3 + 1],
            basePositions[i0 * 3 + 2]
          )
          const v1 = new THREE.Vector3(
            basePositions[i1 * 3],
            basePositions[i1 * 3 + 1],
            basePositions[i1 * 3 + 2]
          )
          const v2 = new THREE.Vector3(
            basePositions[i2 * 3],
            basePositions[i2 * 3 + 1],
            basePositions[i2 * 3 + 2]
          )
          const normal = v1.clone().sub(v2).cross(v0.clone().sub(v2)).normalize()
          const worldPos = new THREE.Vector3(cx, cy, cz)
          const worldNormal = normal.clone()
          heroCube.localToWorld(worldPos)
          worldNormal.transformDirection(heroCube.matrixWorld)
          spawnHeroDebris(worldPos, worldNormal, 1.1)
          triggerHeroShake(0.1)
        }
        for (const i of [i0, i1, i2]) {
          const ix = i * 3
          if (removed) {
            posAttr.array[ix] = 0
            posAttr.array[ix + 1] = 0
            posAttr.array[ix + 2] = 0
          } else {
            posAttr.array[ix] = basePositions[ix]
            posAttr.array[ix + 1] = basePositions[ix + 1]
            posAttr.array[ix + 2] = basePositions[ix + 2]
          }
        }
      }
      posAttr.needsUpdate = true
    }

    if (scaleApplied) {
      heroCube.scale.copy(scaleVec)
    } else if (heroCube.userData.baseScale) {
      heroCube.scale.lerp(heroCube.userData.baseScale, 0.15)
    }

    // Keep UVs fixed so the texture follows mesh deformation naturally.

    if (heroState === 'closing') {
      const duration = heroMode === 'shoot' ? HERO_TRANSITION_SEC_SHOOT : HERO_TRANSITION_SEC
      const u = (t - heroTransitionStart) / duration
      const eased = easeOutCubic(Math.max(0, Math.min(1, u)))
      const cubeU = (t - heroTransitionStart) / (duration * 0.35)
      const cubeEased = easeOutCubic(Math.max(0, Math.min(1, cubeU)))
      heroCube.position.lerpVectors(heroTargetPos, heroBasePos, cubeEased)
      heroCube.rotation.x = heroSpinStartRot.x + Math.PI * 2 * heroSpinTurns * (1 - cubeEased)
      heroCube.rotation.y = heroSpinStartRot.y + Math.PI * 1.4 * (1 - cubeEased)
      const baseScale = heroCube.userData.baseScale ?? heroCube.scale
      heroCube.scale.copy(baseScale).multiplyScalar(1 - cubeEased)
      camera.position.lerpVectors(heroCameraFromPosition, controlsDefaults.position, eased)
      controls.target.lerpVectors(heroCameraFromTarget, controlsDefaults.target, eased)
      if (heroDonutMesh) heroDonutMesh.position.lerpVectors(heroTargetPos, heroBasePos, cubeEased)
      if (heroCube.userData.faceMaterials && mobileQuery.matches && eased > HERO_RETURN_OPACITY_START) {
        const fadeT = (eased - HERO_RETURN_OPACITY_START) / (1 - HERO_RETURN_OPACITY_START)
        for (const mat of heroCube.userData.faceMaterials) {
          mat.transparent = true
          mat.opacity = 1 - fadeT
        }
      }
      if (cubeEased >= 1 && heroCube.visible) {
        heroCube.visible = false
        if (heroDonutMesh) heroDonutMesh.visible = false
      }
      if (u >= 1) {
        heroState = 'idle'
        if (heroClosePlane) heroClosePlane.visible = false
        if (donutMesh) donutMesh.visible = true
        if (linkMeshes.length) {
          for (const mesh of linkMeshes) mesh.visible = true
        }
        if (nameMeshRef) nameMeshRef.visible = true
        if (subtitleMeshRef) subtitleMeshRef.visible = true
        restoreControls()
        heroCube.rotation.copy(heroSpawnBaseRotation)
        heroCube.userData.baseRotation.copy(heroSpawnBaseRotation)
        if (nameHitPlane) nameHitPlane.visible = true
        if (heroCube.userData.faceMaterials) {
          for (const mat of heroCube.userData.faceMaterials) {
            mat.transparent = false
            mat.opacity = 1
          }
        }
      }
    }
    if (Array.isArray(heroCube.material)) {
      for (let i = 0; i < heroCube.material.length; i++) {
        const mat = heroCube.material[i]
        if (!mat?.map) continue
        mat.color.setRGB(1, 1, 1)
      }
    }
  }

  updateHeroDebris(delta)
  updateHeroSparks(delta)

  if (ratedRoot && ratedReady) {
    const inHudScene =
      aboutState === 'closed' &&
      galleryState === 'closed' &&
      reelState === 'closed' &&
      videosState === 'closed' &&
      heroState === 'idle'
    if (!ratedDragActive) {
      const baseRot = ratedRoot.userData.baseRotation
      if (baseRot) {
        ratedAngularVelocity.x += (baseRot.x - ratedRoot.rotation.x) * 0.06
        ratedAngularVelocity.y += (baseRot.y - ratedRoot.rotation.y) * 0.06
        ratedAngularVelocity.multiplyScalar(0.88)
        ratedRoot.rotation.x += ratedAngularVelocity.x
        ratedRoot.rotation.y += ratedAngularVelocity.y
      }
    }
    if (ratedSquishActive) {
      const u = (t - ratedSquishStart) / HERO_SQUISH_DURATION
      if (u >= 1) {
        ratedSquishActive = false
      } else {
        const damp = Math.exp(-ratedSquishDamp * u)
        const wobble = Math.sin(u * Math.PI * ratedSquishWobble) * damp
        const squash = ratedSquishAmount * wobble
        const baseScale = ratedRoot.userData.baseScale ?? ratedRoot.scale
        ratedRoot.scale.set(
          baseScale.x * (1 + squash * 0.6),
          baseScale.y * (1 - squash),
          baseScale.z * (1 + squash * 0.6)
        )
      }
    } else if (ratedRoot.userData.baseScale) {
      ratedRoot.scale.lerp(ratedRoot.userData.baseScale, 0.15)
    }
  }

  if (ratedRoot && ratedReady) {
    if (ratedState === 'focusing') {
      const u = (t - ratedFocusStart) / RATED_FOCUS_DURATION
      const eased = easeOutCubic(Math.max(0, Math.min(1, u)))
      ratedRoot.scale.setScalar(THREE.MathUtils.lerp(ratedFocusFromScale, ratedFocusTargetScale, eased))
      camera.position.lerpVectors(ratedFocusFromPosition, ratedFocusCameraPos, eased)
      controls.target.lerpVectors(ratedFocusFromTarget, ratedFocusTargetPos, eased)
      if (u >= 1) ratedState = 'open'
    } else if (ratedState === 'closing') {
      const u = (t - ratedFocusStart) / RATED_FOCUS_DURATION
      const eased = easeOutCubic(Math.max(0, Math.min(1, u)))
      ratedRoot.scale.setScalar(THREE.MathUtils.lerp(ratedFocusFromScale, ratedRoot.userData.baseScale?.x ?? ratedFocusFromScale, eased))
      camera.position.lerpVectors(ratedFocusFromPosition, controlsDefaults.position, eased)
      controls.target.lerpVectors(ratedFocusFromTarget, controlsDefaults.target, eased)
      if (u >= 1) {
        ratedState = 'idle'
        if (ratedSpawnRot) ratedRoot.rotation.copy(ratedSpawnRot)
        if (donutMesh) donutMesh.visible = true
        if (linkMeshes.length) {
          for (const mesh of linkMeshes) mesh.visible = true
        }
        if (nameMeshRef) nameMeshRef.visible = true
        if (subtitleMeshRef) subtitleMeshRef.visible = true
        restoreControls()
      }
    }
  }

  if (aboutDonutMesh && aboutDonutMesh.visible && aboutDonuts.length) {
    for (let i = 0; i < aboutDonuts.length; i++) {
      const donut = aboutDonuts[i]
      donut.rotation.y += delta * donut.rotSpeed
      donut.rotation.x += delta * donut.rotSpeed * 0.6
      aboutDonutDummy.position.copy(donut.basePos)
      aboutDonutDummy.rotation.copy(donut.rotation)
      aboutDonutDummy.scale.copy(donut.scale)
      aboutDonutDummy.updateMatrix()
      aboutDonutMesh.setMatrixAt(i, aboutDonutDummy.matrix)
    }
    aboutDonutMesh.instanceMatrix.needsUpdate = true
  }

  // Smooth hover scaling for interactive meshes
  for (const mesh of interactive) {
    if (mesh.isInstancedMesh) continue
    if (!mesh.userData.baseScale) continue
    if (mesh === heroCube && (heroSquishActive || heroDragActive)) continue
    const target = mesh.userData.targetScale ?? 1
    hoverScaleTarget.copy(mesh.userData.baseScale).multiplyScalar(target)
    mesh.scale.lerp(hoverScaleTarget, 0.18)
  }


  // Float link texts
  for (const item of floatingTexts) {
    const { mesh, basePos, speed, amp, phase, rotAmp } = item
    mesh.position.copy(basePos).add(mesh.userData.startupOffset ?? new THREE.Vector3(0, 0, 0))
    mesh.position.y += Math.sin(t * speed + phase) * amp
    const baseRot = mesh.userData.baseRotation ?? new THREE.Euler()
    mesh.rotation.y = baseRot.y + Math.sin(t * speed * 0.6 + phase) * rotAmp
    mesh.rotation.x = baseRot.x
    mesh.rotation.z = baseRot.z
    const spin = mesh.userData.spin
    if (spin?.active) {
      const u = (t - spin.t0) / spin.duration
      if (u >= 1) {
        mesh.rotation.x = spin.baseRotX
        spin.active = false
      } else {
        const eased = easeInOutCubic(Math.max(0, Math.min(1, u)))
        mesh.rotation.x = spin.startRotX + (Math.PI * 2) * eased
      }
    }
  }

  for (const mesh of startupTextMeshes) {
    if (floatingTexts.find((item) => item.mesh === mesh)) continue
    const basePos = mesh.userData.basePos ?? mesh.position
    mesh.position.copy(basePos).add(mesh.userData.startupOffset ?? new THREE.Vector3(0, 0, 0))
  }

  // Donut spread (instanced)
  if (donutMesh && donuts.length) {
    for (let i = 0; i < donuts.length; i++) {
      const donut = donuts[i]
      donutTarget.copy(donut.basePos).add(donut.spreadOffset)
      donut.pos.lerp(donutTarget, 0.08)
      const spin = donut.spin
      if (spin?.active) {
        const u = (t - spin.t0) / spin.duration
        if (u >= 1) {
          donut.rotation.x = spin.baseRotX
          spin.active = false
          spin.isStartup = false
        } else {
          const eased = spin.isStartup
            ? easeOutCubic(Math.max(0, Math.min(1, u)))
            : easeInOutCubic(Math.max(0, Math.min(1, u)))
          donut.rotation.x = spin.startRotX + (Math.PI * 2) * eased
        }
      }

      donutDummy.position.copy(donut.pos)
      donutDummy.rotation.copy(donut.rotation)
      donutDummy.scale.copy(donut.scale)
      donutDummy.updateMatrix()
      donutMesh.setMatrixAt(i, donutDummy.matrix)
    }
    donutMesh.instanceMatrix.needsUpdate = true
  }
  for (const image of images) {
    imageTarget.copy(image.userData.basePos).add(image.userData.spreadOffset)
    image.position.lerp(imageTarget, 0.08)
  }

  // Subtitle 360 spin on click
  if (subtitleSpin.active && subtitleMeshRef) {
    const u = (t - subtitleSpin.t0) / subtitleSpin.duration
    if (u >= 1) {
      subtitleMeshRef.rotation.x = subtitleSpin.baseRotX
      subtitleSpin.active = false
    } else {
      const eased = easeInOutCubic(Math.max(0, Math.min(1, u)))
      subtitleMeshRef.rotation.x = subtitleSpin.startRotX + (Math.PI * 2) * eased
    }
  }

  if (introActive) {
    const u = (t - introStart) / introDuration
    const eased = easeOutCubic(Math.max(0, Math.min(1, u)))
    camera.position.lerpVectors(introFromPosition, cameraBasePosition, eased)
    controls.target.lerpVectors(introFromTarget, anchor.name, eased)
    if (u >= 1) {
      introActive = false
      camera.position.copy(cameraBasePosition)
      controls.target.copy(anchor.name)
      computeRatedSpawnTargetFrom(camera.position, controls.target)
      if (ratedRoot && ratedReady && ratedState === 'idle') updateRatedSpawnPose()
    }
  }

  if (
    pendingFitText &&
    aboutState === 'closed' &&
    galleryState === 'closed' &&
    reelState === 'closed' &&
    videosState === 'closed'
  ) {
    pendingFitText = false
    fitTextToViewport()
  }

  if (heroComboCount > 0 && t > heroComboDecayUntil) {
    heroComboCount = 0
  }

  controls.update()
  renderer.render(scene, camera)
  requestAnimationFrame(tick)
}

tick()
